import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Envelope, MagnifyingGlass, Trophy, CheckCircle, XCircle, ClockClockwise, ChartBar,
  ArrowsClockwise, FloppyDisk, ArrowDown, ArrowUp, UploadSimple, Sparkle, Plus, X,
  Calculator, Info, Truck, Warning, FilePdf, FileXls, DownloadSimple, Handshake, Calendar, Package,
  ClipboardText, Polygon,
} from "@phosphor-icons/react";
import { fmtDate } from "@/lib/dateUtils";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin } from "@phosphor-icons/react";
import { titleCaseTR, toLowerTR, digitsOnly } from "@/lib/textFormat";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Legend, Tooltip,
} from "recharts";

const RESPONSE_META = {
  beklemede: { label: "Beklemede", cls: "bg-amber-100 text-amber-800", icon: ClockClockwise },
  verildi: { label: "Verildi", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  verilmedi: { label: "Verilmedi", cls: "bg-red-100 text-red-700", icon: XCircle },
  kabul_edildi: { label: "Kabul Edildi", cls: "bg-[#1B4776] text-white", icon: Trophy },
  reddedildi: { label: "Reddedildi", cls: "bg-slate-200 text-slate-600", icon: XCircle },
};

const fmtTL = (n) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 })
    .format(Number(n) || 0);

export default function RFQOffers() {
  const nav = useNavigate();
  const { sid } = useParams();                       // şantiye bazlı sayfa
  const { sites, activeSiteId } = useSites();
  const site = useMemo(() => (sid === "merkez"
    ? { code: "MERKEZ", name: "Merkez / Genel" }
    : (sites || []).find((x) => x.id === sid)), [sites, sid]);
  const [searchParams, setSearchParams] = useSearchParams();
  const rfqFilterId = searchParams.get("rfq") || "";
  const rfqFilterCode = searchParams.get("code") || "";
  const askConfirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [material, setMaterial] = useState("");
  const [materialOptions, setMaterialOptions] = useState([]);
  const [response, setResponse] = useState("all");
  const [sort, setSort] = useState({ key: "rfq_code", dir: "asc" });
  const [loading, setLoading] = useState(false);
  // Teklif Gir dialog
  const [entry, setEntry] = useState(null); // {rfq, row}
  const [entryForm, setEntryForm] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [matchedInfo, setMatchedInfo] = useState([]); // AI eşleştirme sonuçları
  // KDV oranları (ayarlar)
  const [vatRates, setVatRates] = useState([1, 10, 20]);
  const [showAddVat, setShowAddVat] = useState(false);
  const [newVatRate, setNewVatRate] = useState("");
  // Karşılaştır dialog + TVM parametreleri
  const [compareData, setCompareData] = useState(null);
  const [compareRfqId, setCompareRfqId] = useState(null);
  const [tvmTarget, setTvmTarget] = useState(0); // hedef vade (gün)
  const [tvmMonthly, setTvmMonthly] = useState(0); // aylık faiz oranı (ondalık: 0.05 = %5)
  // Firma kaydı modal (Kabul Et anında supplier_id yoksa)
  const [supplierModal, setSupplierModal] = useState(null); // {recipientIndex, form:{...}}
  const [allSuppliers, setAllSuppliers] = useState([]); // duplicate check için

  // Fiyat Dışı Unsurlar — panel state
  const [npcPanelOpen, setNpcPanelOpen] = useState(false);
  const [npcDraft, setNpcDraft] = useState(""); // yeni kriter için input
  const [npcDraftGroup, setNpcDraftGroup] = useState("teknik"); // mali | teknik
  const [npcDraftWeight, setNpcDraftWeight] = useState("");     // %
  // Faz 20.16
  const [npcDraftType, setNpcDraftType] = useState("subjective"); // subjective | numeric
  const [npcDraftDir, setNpcDraftDir] = useState("lower_better"); // lower | higher_better
  const [npcDraftUnit, setNpcDraftUnit] = useState("");           // "kg/m²", "gün" vb.
  const [npcSaving, setNpcSaving] = useState(false);
  // Faz 20.18 — RFQ seviyesi Teknik/Mali grup ağırlıkları
  const [ahpTeknikW, setAhpTeknikW] = useState(80);
  const [ahpMaliW, setAhpMaliW] = useState(20);
  const [ahpWeightSaving, setAhpWeightSaving] = useState(false);

  // Firma listesini bir kez yükle (duplicate kontrol için)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/suppliers`);
        setAllSuppliers(data);
      } catch (_) { /* sessiz */ }
    })();
  }, []);

  // Türkçe küçültme (duplicate karşılaştırma için)
  const _fold = (s) => (s || "").toString().trim().toLowerCase()
    .replace(/[ıİ]/g, "i").replace(/[çÇ]/g, "c").replace(/[ğĞ]/g, "g")
    .replace(/[öÖ]/g, "o").replace(/[şŞ]/g, "s").replace(/[üÜ]/g, "u");

  // supplierModal.form.tax_number veya name değiştikçe duplicate'i bul
  const duplicateSupplier = useMemo(() => {
    if (!supplierModal) return null;
    const f = supplierModal.form;
    const vkn = (f.tax_number || "").replace(/\D/g, "");
    // 1) VKN ile eşleşme (öncelikli — kesin ipucu)
    if (vkn && vkn.length >= 10) {
      const found = allSuppliers.find(s => (s.tax_number || "").replace(/\D/g, "") === vkn);
      if (found) return { match: found, reason: "vkn" };
    }
    // 2) Firma adı ile TAM eşleşme (case/aksan bağımsız)
    const nameFold = _fold(f.name);
    if (nameFold && nameFold.length >= 3) {
      const found = allSuppliers.find(s => _fold(s.name) === nameFold);
      if (found) return { match: found, reason: "name" };
    }
    return null;
  }, [supplierModal, allSuppliers]);

  const useExistingSupplier = async () => {
    if (!supplierModal || !duplicateSupplier) return;
    const existing = duplicateSupplier.match;
    const j = supplierModal.recipientIndex;
    const rec = compareData.recipients[j];
    try {
      await axios.put(`${API}/rfqs/${compareRfqId}/recipients/${j}`, {
        ...rec,
        supplier_id: existing.id,
        supplier_name: existing.name,
        contact_person: supplierModal.form.contact_person || existing.contact_person || rec.contact_person || "",
      });
      toast.success(`${existing.name} firması bağlandı.`);
      const { data: cmp } = await axios.get(`${API}/rfqs/${compareRfqId}/compare`, {
        params: { target_vade_days: tvmTarget || 0, monthly_rate: tvmMonthly || 0 },
      });
      setCompareData(cmp);
      setSupplierModal(null);
      setTimeout(() => finalizeAccept(j), 100);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      // URL'deki şantiye önceliği; yoksa global aktif şantiye
      const scopeSid = sid || (activeSiteId && activeSiteId !== "all" ? activeSiteId : null);
      if (scopeSid) params.site_id = scopeSid;
      if (rfqFilterId) params.rfq_id = rfqFilterId;
      if (response !== "all") params.response = response;
      if (q) params.q = q;
      if (material) params.material = material;
      const { data } = await axios.get(`${API}/rfqs/offers`, { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeSiteId, sid, response, rfqFilterId]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, material]);

  // Malzeme autocomplete kaynağı
  useEffect(() => {
    (async () => {
      try {
        const params = {};
        const scopeSid = sid || (activeSiteId && activeSiteId !== "all" ? activeSiteId : null);
        if (scopeSid) params.site_id = scopeSid;
        const { data } = await axios.get(`${API}/rfqs/materials/distinct`, { params });
        setMaterialOptions(data?.materials || []);
      } catch (e) { /* sessiz */ }
    })();
  }, [activeSiteId, sid]);

  // KDV oranlarını yükle
  const loadVatRates = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/vat-rates`);
      setVatRates(data.rates || [1, 10, 20]);
    } catch (e) { /* sessiz */ }
  };
  useEffect(() => { loadVatRates(); }, []);

  const addVatRate = async () => {
    const v = parseFloat(newVatRate);
    if (!(v >= 0)) { toast.error("Geçerli bir KDV oranı girin"); return; }
    const next = Array.from(new Set([...vatRates, v])).sort((a, b) => a - b);
    try {
      const { data } = await axios.post(`${API}/settings/vat-rates`, { rates: next });
      setVatRates(data.rates);
      setNewVatRate("");
      setShowAddVat(false);
      toast.success(`%${v} eklendi`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const sortedRows = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av || "").localeCompare(String(bv || ""), "tr") * dir;
    });
  }, [rows, sort]);

  const kpi = useMemo(() => {
    const c = { total: rows.length, beklemede: 0, verildi: 0, verilmedi: 0 };
    for (const r of rows) {
      const k = ["kabul_edildi", "reddedildi"].includes(r.response) ? "verildi" : (r.response || "beklemede");
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [rows]);

  const changeSort = (key) => {
    setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const SortIcon = ({ col }) => {
    if (sort.key !== col) return <ArrowDown size={10} className="opacity-20 inline ml-1" />;
    return sort.dir === "asc"
      ? <ArrowUp size={10} className="inline ml-1" />
      : <ArrowDown size={10} className="inline ml-1" />;
  };

  const openEntry = async (row) => {
    try {
      const { data: rfq } = await axios.get(`${API}/rfqs/${row.rfq_id}`);
      const rec = rfq.recipients[row.recipient_index];
      const nItems = (rfq.items || []).length;
      setEntry({ rfq, row });
      setEntryForm({
        response: rec.response || "verildi",
        unit_prices: Array.from({ length: nItems }, (_, i) => rec.unit_prices?.[i] ?? ""),
        vat_rates: Array.from({ length: nItems }, (_, i) => rec.vat_rates?.[i] ?? (rfq.items[i]?.vat_rate ?? 20)),
        brands: Array.from({ length: nItems }, (_, i) => rec.brands?.[i] ?? ""),
        total_price: rec.total_price || 0,
        offered_delivery_date: rec.offered_delivery_date || "",
        offered_payment_terms: rec.offered_payment_terms || "",
        payment_term_days: rec.payment_term_days || 0,
        validity_days: rec.validity_days || 0,
        lead_time_days: rec.lead_time_days || 0,
        attachment_path: rec.attachment_path || "",
        note: rec.note || "",
        // Nakliye
        shipping_included: rec.shipping_included !== false,
        shipping_unit: rec.shipping_unit || "sefer",
        shipping_unit_price: rec.shipping_unit_price || 0,
        shipping_quantity: rec.shipping_quantity || 0,
        shipping_vat_rate: rec.shipping_vat_rate ?? 20,
        shipping_payment_term_days: rec.shipping_payment_term_days || 0,
        shipping_note: rec.shipping_note || "",
      });
      setMatchedInfo([]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const parseOfferFile = async (file) => {
    if (!entry || !file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/rfqs/parse-offer?rfq_id=${entry.rfq.id}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Yüklenen orijinal dosyayı da sakla
      let attachmentPath = entry?.row?.attachment_path || "";
      try {
        const fd2 = new FormData();
        fd2.append("file", file);
        const up = await axios.post(`${API}/upload`, fd2, { headers: { "Content-Type": "multipart/form-data" } });
        attachmentPath = up.data.path;
      } catch (_) { /* upload başarısız olsa da parse sonucu kullanılır */ }

      setEntryForm(f => {
        // AI'dan gelen brands: RFQ item sırasına göre. Boş gelenler için mevcut değeri koru.
        const aiBrands = Array.isArray(data.brands) ? data.brands : null;
        const nextBrands = (f.brands || []).map((cur, i) => {
          const ai = aiBrands?.[i];
          return (ai && String(ai).trim()) ? String(ai).trim() : (cur || "");
        });
        return {
        ...f,
        unit_prices: (data.unit_prices || f.unit_prices).map(v => (v != null ? String(v) : "")),
        vat_rates: (data.vat_rates || f.vat_rates).map((v, i) => (v && v > 0 ? v : (f.vat_rates[i] ?? 20))),
        brands: nextBrands,
        offered_delivery_date: data.offered_delivery_date || f.offered_delivery_date,
        offered_payment_terms: data.payment_terms_text || f.offered_payment_terms,
        payment_term_days: (data.payment_term_days ?? f.payment_term_days) || 0,
        validity_days: data.validity_days || f.validity_days,
        response: "verildi",
        attachment_path: attachmentPath,
        // Nakliye — AI dolduruyorsa güncelle
        shipping_included: data.shipping_included !== null && data.shipping_included !== undefined ? data.shipping_included : f.shipping_included,
        shipping_unit: data.shipping_unit || f.shipping_unit,
        shipping_unit_price: data.shipping_unit_price ?? f.shipping_unit_price,
        shipping_quantity: data.shipping_quantity ?? f.shipping_quantity,
        shipping_vat_rate: data.shipping_vat_rate ?? f.shipping_vat_rate,
        shipping_payment_term_days: data.shipping_payment_term_days ?? f.shipping_payment_term_days,
        shipping_note: data.shipping_note || f.shipping_note,
        // Ortalama vade bilgisi (UI notu için)
        _avg_vade_days: data.computed_avg_vade_days || null,
        _avg_vade_series: data.computed_avg_vade_series || [],
        };
      });
      setMatchedInfo(data.matched_items || []);
      const matched = (data.matched_items || []).filter(m => m.matched).length;
      const total = (entry.rfq.items || []).length;
      const brandCount = (data.brands || []).filter(b => b && String(b).trim()).length;
      const qtyWarn = (data.matched_items || []).filter(m => m.matched && Math.abs(m.quantity_diff_pct || 0) > 0).length;
      const avgVade = data.computed_avg_vade_days;
      const avgSeries = data.computed_avg_vade_series || [];
      let toastMsg = `AI ${matched}/${total} kalemi eşleştirdi.`;
      if (brandCount > 0) toastMsg += ` ${brandCount} marka çıkarıldı.`;
      if (qtyWarn > 0) toastMsg += ` ${qtyWarn} kalemde miktar farkı — kontrol edin.`;
      if (avgVade && avgSeries.length >= 2) {
        toastMsg += ` Vade: [${avgSeries.join(", ")}] → ortalama ${avgVade} gün.`;
      }
      if (qtyWarn > 0) toast.warning(toastMsg);
      else toast.success(toastMsg);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setParsing(false); }
  };

  const saveEntry = async () => {
    if (!entry || !entryForm) return;
    const payload = {
      ...entry.rfq.recipients[entry.row.recipient_index],
      response: entryForm.response,
      unit_prices: entryForm.unit_prices.map(v => parseFloat(v) || 0),
      vat_rates: entryForm.vat_rates.map(v => parseFloat(v) || 0),
      brands: (entryForm.brands || []).map(b => (b || "").toString().trim()),
      offered_delivery_date: entryForm.offered_delivery_date,
      offered_payment_terms: entryForm.offered_payment_terms,
      payment_term_days: parseInt(entryForm.payment_term_days) || 0,
      validity_days: parseInt(entryForm.validity_days) || 0,
      lead_time_days: parseInt(entryForm.lead_time_days) || 0,
      attachment_path: entryForm.attachment_path || "",
      note: entryForm.note,
      // Nakliye
      shipping_included: !!entryForm.shipping_included,
      shipping_unit: entryForm.shipping_unit || "sefer",
      shipping_unit_price: parseFloat(entryForm.shipping_unit_price) || 0,
      shipping_quantity: parseFloat(entryForm.shipping_quantity) || 0,
      shipping_vat_rate: parseFloat(entryForm.shipping_vat_rate) || 0,
      shipping_payment_term_days: parseInt(entryForm.shipping_payment_term_days) || 0,
      shipping_note: entryForm.shipping_note || "",
    };
    try {
      await axios.put(`${API}/rfqs/${entry.rfq.id}/recipients/${entry.row.recipient_index}`, payload);
      toast.success("Teklif kaydedildi");
      setEntry(null); setEntryForm(null); setMatchedInfo([]);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openCompare = async (rfq_id) => {
    setCompareRfqId(rfq_id);
    try {
      const { data } = await axios.get(`${API}/rfqs/${rfq_id}/compare`, {
        params: { target_vade_days: tvmTarget || 0, monthly_rate: tvmMonthly || 0 },
      });
      setCompareData(data);
      // Faz 20.18 — grup ağırlıklarını yansıt
      const w = data?.rfq?.ahp_weights || {};
      if (w.teknik_pct != null) setAhpTeknikW(parseFloat(w.teknik_pct) || 0);
      if (w.mali_pct != null) setAhpMaliW(parseFloat(w.mali_pct) || 0);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Faz 20.18 — Grup ağırlıklarını backend'e kaydet ve compare'ı yenile
  const saveAhpWeights = async (nextTek, nextMal) => {
    if (!compareRfqId) return;
    if ((nextTek + nextMal) <= 0) { toast.warning("En az bir grup ağırlığı 0'dan büyük olmalı"); return; }
    setAhpWeightSaving(true);
    try {
      await axios.put(`${API}/rfqs/${compareRfqId}/ahp-weights`, {
        teknik_pct: nextTek, mali_pct: nextMal,
      });
      const { data: fresh } = await axios.get(`${API}/rfqs/${compareRfqId}/compare`, {
        params: { target_vade_days: tvmTarget || 0, monthly_rate: tvmMonthly || 0 },
      });
      setCompareData(fresh);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAhpWeightSaving(false); }
  };

  const acceptOffer = async (j) => {
    if (!compareData || !compareRfqId) return;
    const t = compareData.totals[j];
    const recipient = compareData.recipients[j];
    // supplier_id yoksa önce firma kayıt modal'ı aç
    if (!recipient?.supplier_id) {
      setSupplierModal({
        recipientIndex: j,
        form: {
          name: recipient?.supplier_name || "",
          supplier_type: "tedarikci",
          title: "",
          tax_office: "",
          tax_number: "",
          contact_person: recipient?.contact_person || "",
          phone: "",
          email: "",
          website: "",
          address: "",
          notes: "",
        },
      });
      return;
    }
    await finalizeAccept(j);
  };

  const finalizeAccept = async (j) => {
    if (!compareData || !compareRfqId) return;
    const t = compareData.totals[j];
    const supplierName = t.supplier_name;
    const ok = await askConfirm({
      title: "Teklifi Kabul Et ve Sözleşme Oluştur",
      description: `${supplierName} firmasının teklifi kabul edilecek ve ${(compareData.items || []).length} kalem için ayrı sözleşme (SOZ-YYYYAA-###) otomatik oluşturulacak. RFQ kapatılacak, diğer firmalara "reddedildi" yazılacak. Devam edilsin mi?`,
      confirmText: "Evet, Kabul Et",
      variant: "default",
    });
    if (!ok) return;
    try {
      const { data } = await axios.post(`${API}/rfqs/${compareRfqId}/accept-offer`, {
        recipient_index: j,
      });
      toast.success(`${data.count} sözleşme oluşturuldu: ${data.contracts.map(c => c.code).join(", ")}`);
      setCompareData(null); setCompareRfqId(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveSupplierAndContinue = async () => {
    if (!supplierModal) return;
    const f = supplierModal.form;
    if (!f.name || !f.name.trim()) {
      toast.error("Firma adı zorunludur");
      return;
    }
    // VKN kontrolü (opsiyonel — girilmişse 10 veya 11 hane olmalı)
    const tn = digitsOnly(f.tax_number, 11);
    if (tn && tn.length !== 10 && tn.length !== 11) {
      toast.error("VKN 10 (şirket) veya 11 (şahıs) hane olmalıdır");
      return;
    }
    try {
      // 1) Firmayı kaydet (yazım kuralları uygulanır)
      const { data: newSup } = await axios.post(`${API}/suppliers`, {
        name: titleCaseTR(f.name.trim()),
        supplier_type: f.supplier_type || "tedarikci",
        title: titleCaseTR(f.title || ""),
        tax_office: titleCaseTR(f.tax_office || ""),
        tax_number: tn,
        contact_person: titleCaseTR(f.contact_person || ""),
        phone: f.phone || "",
        email: toLowerTR(f.email || ""),
        website: toLowerTR(f.website || ""),
        address: titleCaseTR(f.address || ""),
        notes: titleCaseTR(f.notes || ""),
      });
      // 2) RFQ recipient'ı yeni supplier_id ile güncelle
      const j = supplierModal.recipientIndex;
      const rec = compareData.recipients[j];
      await axios.put(`${API}/rfqs/${compareRfqId}/recipients/${j}`, {
        ...rec,
        supplier_id: newSup.id,
        supplier_name: newSup.name,
        contact_person: f.contact_person || rec.contact_person || "",
      });
      toast.success(`${newSup.name} firma olarak kaydedildi.`);
      // Firma listesini de yenile (bir sonraki duplicate kontrol için)
      setAllSuppliers(prev => [...prev, newSup].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")));
      // 3) compareData'yı yenile ki supplier_id dolsun
      const { data: cmp } = await axios.get(`${API}/rfqs/${compareRfqId}/compare`, {
        params: { target_vade_days: tvmTarget || 0, monthly_rate: tvmMonthly || 0 },
      });
      setCompareData(cmp);
      setSupplierModal(null);
      // 4) Kabul akışına devam
      setTimeout(() => finalizeAccept(j), 100);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Vade metninden sıralı vade serisi parse eder ("60-90-120-150 gün" -> [60,90,120,150])
  const parseVadeSeries = (text) => {
    if (!text) return null;
    const low = text.toLowerCase();
    const m = low.match(/((?:\d{1,3})(?:\s*[-/,]\s*\d{1,3}){1,})\s*(?:gün|vade|g[uü]nl[uü])/);
    if (!m) return null;
    const nums = (m[1].match(/\d+/g) || []).map(Number).filter(n => n <= 365);
    if (nums.length < 2) return null;
    return nums;
  };

  // "%30 peşin, kalanı 60 gün" veya "%40 peşin + 60 gün vade" gibi karma yapıları parse eder.
  // Dönüş: [{pct: 0.30, days: 0}, {pct: 0.70, days: 60}] (toplamı 1.0 olur) veya null
  const parsePercentSplit = (text) => {
    if (!text) return null;
    const low = text.toLowerCase();
    // "%N peşin" veya "N% peşin" veya "N peşin" pattern
    const pesinMatch = low.match(/%?\s*(\d{1,3})\s*%?\s*p(?:eşin|esin)/);
    if (!pesinMatch) return null;
    const pesinPct = parseInt(pesinMatch[1]);
    if (!(pesinPct > 0 && pesinPct < 100)) return null;
    // "kalanı N gün" veya "kalan N gün" veya "gerisi N gün"
    const kalanMatch = low.match(/(?:kalan[ıi]?|gerisi|kalani)\s*(?:.*?)(\d{1,3})\s*g[uü]n/);
    // Ya da "N% peşin, N% M gün" pattern (%30 peşin, %70 60 gün)
    // Ya da "peşin + M gün" pattern (peşinden sonra tek taksit)
    if (kalanMatch) {
      const kalanDays = parseInt(kalanMatch[1]);
      if (kalanDays >= 0 && kalanDays <= 365) {
        return [
          { pct: pesinPct / 100, days: 0 },
          { pct: (100 - pesinPct) / 100, days: kalanDays },
        ];
      }
    }
    // Alternatif: "%30 peşin + %70 60 gün"
    const altMatch = low.match(/%?\s*(\d{1,3})\s*%?\s*(?:.*?)(\d{1,3})\s*g[uü]n/);
    if (altMatch && parseInt(altMatch[1]) !== pesinPct) {
      const restPct = parseInt(altMatch[1]);
      const restDays = parseInt(altMatch[2]);
      if (restPct + pesinPct === 100 && restDays > 0) {
        return [
          { pct: pesinPct / 100, days: 0 },
          { pct: restPct / 100, days: restDays },
        ];
      }
    }
    return null;
  };

  // Öngörülen Ödeme Takvimi — ay bazında { month: amount } döner
  const buildPaymentSchedule = (t, recipient) => {
    if (!t || t.gross <= 0) return {};
    const schedule = {};
    const matWithVat = (t.material_net || 0) + (t.material_vat || 0);
    const text = recipient?.offered_payment_terms || "";

    // 1) Yüzde bazlı karma yapı ("%30 peşin, kalanı 60 gün")
    const pctSplit = parsePercentSplit(text);
    // 2) Sıralı seri ("60-90-120-150 gün")
    const vadeSeries = pctSplit ? null : parseVadeSeries(text);

    if (pctSplit) {
      pctSplit.forEach(part => {
        const m = Math.max(0, Math.round(part.days / 30));
        schedule[m] = (schedule[m] || 0) + matWithVat * part.pct;
      });
    } else if (vadeSeries && vadeSeries.length >= 2) {
      const per = matWithVat / vadeSeries.length;
      vadeSeries.forEach(days => {
        const m = Math.max(0, Math.round(days / 30));
        schedule[m] = (schedule[m] || 0) + per;
      });
    } else {
      const m = Math.max(0, Math.round((t.payment_term_days || 0) / 30));
      schedule[m] = (schedule[m] || 0) + matWithVat;
    }
    // Nakliye (ayrı ise)
    if (!t.shipping_included && (t.shipping_net || 0) > 0) {
      const shipGross = (t.shipping_net || 0) + (t.shipping_vat || 0);
      const shipDays = t.shipping_payment_term_days || t.payment_term_days || 0;
      const m = Math.max(0, Math.round(shipDays / 30));
      schedule[m] = (schedule[m] || 0) + shipGross;
    }
    return schedule;
  };

  // Tüm firmalar için birleşik takvim
  const paymentTimeline = useMemo(() => {
    if (!compareData) return null;
    const perSupplier = compareData.totals.map((t, j) =>
      ({ j, name: t.supplier_name, gross: t.gross, schedule: buildPaymentSchedule(t, compareData.recipients[j]) })
    ).filter(x => x.gross > 0);
    if (!perSupplier.length) return null;
    // Ortak ay listesi (sıralı, unique)
    const monthSet = new Set();
    perSupplier.forEach(s => Object.keys(s.schedule).forEach(m => monthSet.add(parseInt(m))));
    const months = Array.from(monthSet).sort((a, b) => a - b);
    return { perSupplier, months };
  }, [compareData]);

  // Karşılaştırma verisinde herhangi bir fiyat var mı? — Hesapla butonu için kontrol
  const hasAnyPrice = useMemo(() => {
    if (!compareData) return false;
    return (compareData.totals || []).some(t => (t?.net || 0) > 0 || (t?.gross || 0) > 0);
  }, [compareData]);

  // TVM parametreleri değişince otomatik hesap YAPILMAZ — kullanıcı Hesapla butonuna tıklar
  const recomputeCompare = async () => {
    if (!compareRfqId) return;
    // Hiç fiyat girilmemişse kullanıcıyı bilgilendir — sessiz kalıp yanıltmayalım
    if (!hasAnyPrice) {
      toast.warning("Bu RFQ'da henüz fiyat girilmemiş. Karşılaştırma için önce firmaların birim fiyatlarını girmelisiniz (Teklifi Gir butonundan veya AI ile PDF/foto yükleyerek).");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/rfqs/${compareRfqId}/compare`, {
        params: { target_vade_days: tvmTarget || 0, monthly_rate: tvmMonthly || 0 },
      });
      setCompareData(data);
      if ((tvmTarget || 0) > 0 && (tvmMonthly || 0) > 0) {
        // Yeni "en uygun" firma değişmiş olabilir — kullanıcıya en uygun net ile birlikte göster
        const bestIdx = data.best_supplier_index;
        const bestName = bestIdx >= 0 ? (data.recipients?.[bestIdx]?.supplier_name || "-") : "-";
        const bestAdj = bestIdx >= 0 ? (data.totals?.[bestIdx]?.adjusted_net || 0) : 0;
        toast.success(
          `TVM hesaplandı — Hedef ${tvmTarget} gün, %${(tvmMonthly * 100).toFixed(2)} aylık faiz. En uygun (ayarlı): ${bestName} ${bestAdj ? "· " + fmtTL(bestAdj) : ""}`
        );
      } else {
        toast.info("TVM parametreleri temizlendi — mukayese net fiyata göre yapılıyor.");
      }
    } catch (e) { toast.error(formatErr(e)); }
  };

  // -------- Fiyat Dışı Unsurlar (Non-Price Criteria) --------
  const npcCriteria = compareData?.rfq?.non_price_criteria || [];

  const saveNpcCriteria = async (newList) => {
    if (!compareRfqId) return;
    setNpcSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/rfqs/${compareRfqId}/non-price-criteria`,
        { criteria: newList.map(c => ({
            key: c.key || "",
            label: c.label,
            group: c.group || "teknik",
            weight_pct: c.weight_pct != null ? parseFloat(c.weight_pct) || 0 : 0,
            type: c.type === "numeric" ? "numeric" : "subjective",
            direction: c.direction === "higher_better" ? "higher_better" : "lower_better",
            unit: (c.unit || "").toString().slice(0, 20),
          })) }
      );
      setCompareData(prev => prev ? {
        ...prev,
        rfq: { ...prev.rfq, non_price_criteria: data.non_price_criteria || [] },
      } : prev);
      const { data: fresh } = await axios.get(`${API}/rfqs/${compareRfqId}/compare`, {
        params: { target_vade_days: tvmTarget || 0, monthly_rate: tvmMonthly || 0 },
      });
      setCompareData(fresh);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setNpcSaving(false); }
  };

  const addNpcCriterion = async () => {
    const label = (npcDraft || "").trim();
    if (!label) { toast.warning("Kriter adı boş olamaz"); return; }
    const w = parseFloat(npcDraftWeight) || 0;
    if (w < 0 || w > 100) { toast.warning("Ağırlık 0-100 arası olmalı"); return; }
    const next = [...npcCriteria, {
      label,
      group: npcDraftGroup || "teknik",
      weight_pct: w,
      type: npcDraftType,
      direction: npcDraftDir,
      unit: npcDraftUnit || "",
    }];
    await saveNpcCriteria(next);
    setNpcDraft("");
    setNpcDraftWeight("");
    setNpcDraftUnit("");
  };

  const updateNpcCriterion = async (key, patch) => {
    const next = npcCriteria.map(c => c.key === key ? { ...c, ...patch } : c);
    await saveNpcCriteria(next);
  };

  const removeNpcCriterion = async (key) => {
    const ok = await askConfirm({
      title: "Kriter silinsin mi?",
      description: "Bu kritere ait tüm firma değerleri de silinecek.",
      confirmText: "Sil", variant: "destructive",
    });
    if (!ok) return;
    await saveNpcCriteria(npcCriteria.filter(c => c.key !== key));
  };

  const setNpcValue = async (recipientIdx, criterionKey, value) => {
    if (!compareRfqId) return;
    setCompareData(prev => {
      if (!prev) return prev;
      const recs = [...prev.recipients];
      const curVals = { ...(recs[recipientIdx]?.non_price_values || {}) };
      curVals[criterionKey] = value;
      recs[recipientIdx] = { ...recs[recipientIdx], non_price_values: curVals };
      return { ...prev, recipients: recs };
    });
  };

  // Faz 20.15 — 0-10 arası AHP puanı
  const setNpcScore = async (recipientIdx, criterionKey, rawValue) => {
    if (!compareRfqId) return;
    // Boş bırakıldıysa null olarak sakla (skorlanmamış say)
    let numOrNull;
    if (rawValue === "" || rawValue == null) {
      numOrNull = null;
    } else {
      let n = parseFloat(rawValue);
      if (isNaN(n)) return;
      if (n < 0) n = 0;
      if (n > 10) n = 10;
      numOrNull = n;
    }
    setCompareData(prev => {
      if (!prev) return prev;
      const recs = [...prev.recipients];
      const curScores = { ...(recs[recipientIdx]?.non_price_scores || {}) };
      if (numOrNull == null) {
        delete curScores[criterionKey];
      } else {
        curScores[criterionKey] = numOrNull;
      }
      recs[recipientIdx] = { ...recs[recipientIdx], non_price_scores: curScores };
      return { ...prev, recipients: recs };
    });
  };

  // Faz 20.16 — Sayısal (numeric) kriter için ham değer
  const setNpcNumeric = async (recipientIdx, criterionKey, rawValue) => {
    if (!compareRfqId) return;
    let valOrNull;
    if (rawValue === "" || rawValue == null) {
      valOrNull = null;
    } else {
      const n = parseFloat(rawValue);
      if (isNaN(n)) return;
      valOrNull = n;
    }
    setCompareData(prev => {
      if (!prev) return prev;
      const recs = [...prev.recipients];
      const cur = { ...(recs[recipientIdx]?.non_price_numeric || {}) };
      if (valOrNull == null) { delete cur[criterionKey]; }
      else { cur[criterionKey] = valOrNull; }
      recs[recipientIdx] = { ...recs[recipientIdx], non_price_numeric: cur };
      return { ...prev, recipients: recs };
    });
  };

  const persistNpcValues = async (recipientIdx) => {
    if (!compareRfqId || !compareData) return;
    const rec = compareData.recipients[recipientIdx] || {};
    try {
      await axios.put(
        `${API}/rfqs/${compareRfqId}/recipients/${recipientIdx}/non-price-values`,
        {
          values: rec.non_price_values || {},
          scores: rec.non_price_scores || {},
          numeric: rec.non_price_numeric || {},
        }
      );
      const { data: fresh } = await axios.get(`${API}/rfqs/${compareRfqId}/compare`, {
        params: { target_vade_days: tvmTarget || 0, monthly_rate: tvmMonthly || 0 },
      });
      setCompareData(fresh);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const createNewRevision = async () => {
    if (!compareRfqId || !compareData) return;
    const curRev = parseInt(compareData.revision || 1);
    const nextRev = curRev + 1;
    const ok = await askConfirm({
      title: "Yeni Revizyon Oluştur",
      description: `Bu talep için yeni bir müzakere turu (Rev.${String(nextRev).padStart(2, "0")}) açılacak. Kalem listesi ve firmalar korunacak; fiyat, KDV, teslim tarihi, vade ve nakliye alanları sıfırlanacak. Devam edilsin mi?`,
      confirmText: "Evet, Revizyon Aç",
      variant: "default",
    });
    if (!ok) return;
    try {
      const { data } = await axios.post(`${API}/rfqs/${compareRfqId}/new-revision`);
      toast.success(`Yeni revizyon oluşturuldu: ${data.code}`);
      setCompareData(null); setCompareRfqId(null);
      // Yeni revizyonu RFQForm sayfasında aç
      nav(`/teklif-talepleri/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Entry dialog: canlı hesaplar (malzeme + nakliye; net, KDV, brüt)
  const entryLiveCalc = useMemo(() => {
    if (!entry || !entryForm) return { matNet: 0, matVat: 0, shipNet: 0, shipVat: 0, net: 0, vat: 0, gross: 0 };
    const items = entry.rfq.items || [];
    let matNet = 0, matVat = 0;
    entryForm.unit_prices.forEach((up, i) => {
      const qty = parseFloat(items[i]?.quantity) || 0;
      const lineNet = (parseFloat(up) || 0) * qty;
      matNet += lineNet;
      const vr = parseFloat(entryForm.vat_rates[i]) || 0;
      matVat += lineNet * (vr / 100);
    });
    let shipNet = 0, shipVat = 0;
    if (!entryForm.shipping_included) {
      shipNet = (parseFloat(entryForm.shipping_unit_price) || 0) * (parseFloat(entryForm.shipping_quantity) || 0);
      const svr = parseFloat(entryForm.shipping_vat_rate) || 0;
      shipVat = shipNet * (svr / 100);
    }
    const net = matNet + shipNet;
    const vat = matVat + shipVat;
    return { matNet, matVat, shipNet, shipVat, net, vat, gross: net + vat };
  }, [entry, entryForm]);

  // Site-scoped sayfada "Geri" + şantiye başlığı
  const headerTitle = site ? (
    <div className="flex items-center gap-3">
      <Link to="/gelen-teklifler" className="inline-flex items-center text-slate-500 hover:text-[#1B4776]" data-testid="offers-back-link">
        <ArrowLeft size={18} weight="bold" />
      </Link>
      <div>
        <div className="text-xs font-mono font-bold text-[#1B4776] tracking-wider uppercase">{site.code}</div>
        <div className="text-lg font-bold text-[#0F172A] leading-tight">{site.name}</div>
        {(site.city || site.district) && (
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
            <MapPin size={11} weight="fill" className="text-[#1B4776]/60" />
            {[site.district, site.city].filter(Boolean).join(", ")}
          </div>
        )}
      </div>
    </div>
  ) : "Gelen Teklifler";

  return (
    <Shell
      title={headerTitle}
      subtitle={site ? "Gelen Teklifler" : "Satın Alma Yönetimi"}
      actions={
        <Button variant="outline" onClick={load} data-testid="offers-refresh" className="rounded-none sm:rounded-sm">
          <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
      }
    >
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1B4776] flex items-center justify-center">
              <Envelope size={14} weight="duotone" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Toplam Gönderim</div>
              <div className="text-2xl font-black text-slate-900 font-heading">{kpi.total}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500 flex items-center justify-center">
              <ClockClockwise size={14} weight="duotone" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Beklemede</div>
              <div className="text-2xl font-black text-slate-900 font-heading">{kpi.beklemede || 0}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-600 flex items-center justify-center">
              <CheckCircle size={14} weight="duotone" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Verildi</div>
              <div className="text-2xl font-black text-slate-900 font-heading">{kpi.verildi || 0}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
              <XCircle size={14} weight="duotone" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Verilmedi</div>
              <div className="text-2xl font-black text-slate-900 font-heading">{kpi.verilmedi || 0}</div>
            </div>
          </div>
        </Card>
      </div>

      {rfqFilterId && (
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#1B4776]/10 text-[#1B4776] text-xs font-bold"
             data-testid="offers-rfq-filter-chip">
          <span className="font-mono">{rfqFilterCode || "Talep"}</span> filtresi
          <button onClick={() => setSearchParams({})} data-testid="offers-rfq-filter-clear"
                  className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 hover:bg-[#1B4776] hover:text-white transition-colors">
            <X size={11} weight="bold" /> Kaldır
          </button>
        </div>
      )}
      {/* Filtreler */}
      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="relative md:col-span-5">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="RFQ kodu veya firma ara..." data-testid="offers-search"
                   className="pl-8 rounded-none sm:rounded-sm" />
          </div>
          <div className="relative md:col-span-4">
            <Package size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={material} onChange={e => setMaterial(e.target.value)}
                   placeholder="Malzeme ara (ör: çimento, boya)..."
                   list="offers-material-datalist"
                   data-testid="offers-material-filter"
                   className="pl-8 pr-8 rounded-none sm:rounded-sm" />
            <datalist id="offers-material-datalist">
              {materialOptions.map((m) => <option key={m} value={m} />)}
            </datalist>
            {material && (
              <button onClick={() => setMaterial("")}
                      data-testid="offers-material-clear"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center hover:bg-slate-100 text-slate-500"
                      title="Malzeme filtresini temizle">
                <X size={12} />
              </button>
            )}
          </div>
          <Select value={response} onValueChange={setResponse}>
            <SelectTrigger className="rounded-none sm:rounded-sm md:col-span-3" data-testid="offers-response-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="beklemede">Beklemede</SelectItem>
              <SelectItem value="verildi">Verildi</SelectItem>
              <SelectItem value="verilmedi">Verilmedi</SelectItem>
              <SelectItem value="kabul_edildi">Kabul Edildi</SelectItem>
              <SelectItem value="reddedildi">Reddedildi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Liste */}
      <Card className="rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <th onClick={() => changeSort("rfq_code")} className="text-left py-3 px-3 font-bold cursor-pointer hover:bg-slate-100">
                  Talep No <SortIcon col="rfq_code" />
                </th>
                <th onClick={() => changeSort("request_date")} className="text-left py-3 px-3 font-bold cursor-pointer hover:bg-slate-100">
                  Tarih <SortIcon col="request_date" />
                </th>
                <th onClick={() => changeSort("site_name")} className="text-left py-3 px-3 font-bold cursor-pointer hover:bg-slate-100">
                  Şantiye <SortIcon col="site_name" />
                </th>
                <th onClick={() => changeSort("supplier_name")} className="text-left py-3 px-3 font-bold cursor-pointer hover:bg-slate-100">
                  Firma <SortIcon col="supplier_name" />
                </th>
                <th className="text-left py-3 px-3 font-bold">Kanal</th>
                <th onClick={() => changeSort("response")} className="text-left py-3 px-3 font-bold cursor-pointer hover:bg-slate-100">
                  Durum <SortIcon col="response" />
                </th>
                <th onClick={() => changeSort("total_price")} className="text-right py-3 px-3 font-bold cursor-pointer hover:bg-slate-100">
                  Toplam <SortIcon col="total_price" />
                </th>
                <th onClick={() => changeSort("offered_delivery_date")} className="text-left py-3 px-3 font-bold cursor-pointer hover:bg-slate-100">
                  Teklif Tarihi <SortIcon col="offered_delivery_date" />
                </th>
                <th className="text-right py-3 px-3 font-bold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-16 text-sm text-slate-400">
                    Henüz gönderilmiş teklif talebi yok. Önce Teklif Talepleri sayfasından firmalara talep gönderin.
                  </td>
                </tr>
              ) : (
                sortedRows.map((r, i) => {
                  const rm = RESPONSE_META[r.response] || RESPONSE_META.beklemede;
                  const RmIcon = rm.icon;
                  return (
                    <tr key={`${r.rfq_id}-${r.recipient_index}`}
                        className={`border-b border-slate-100 hover:bg-slate-50/50 ${i % 2 ? "bg-slate-50/30" : ""}`}
                        data-testid={`offer-row-${i}`}>
                      <td className="py-2.5 px-3 font-mono font-bold text-slate-900">
                        <button onClick={() => nav(`/teklif-talepleri/${r.rfq_id}`)}
                                className="hover:underline hover:text-[#1B4776]">
                          {r.rfq_code}
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 text-xs">{fmtDate(r.request_date)}</td>
                      <td className="py-2.5 px-3 text-slate-700 text-xs">{r.site_name || "-"}</td>
                      <td className="py-2.5 px-3 font-medium text-slate-900">{r.supplier_name}
                        {r.contact_person && <div className="text-[10px] text-slate-500">{r.contact_person}</div>}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 text-xs">{r.channel || "-"}</td>
                      <td className="py-2.5 px-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${rm.cls}`}>
                          <RmIcon size={11} weight="fill" /> {rm.label}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900">
                        {r.total_price ? (
                          <>
                            {fmtTL(r.total_price)}
                            {!r.shipping_included && r.shipping_net > 0 && (
                              <div className="text-[9px] text-slate-500 font-normal flex items-center justify-end gap-1">
                                <Truck size={9} weight="fill" className="text-[#1B4776]" />
                                +{fmtTL(r.shipping_net)} nakliye
                              </div>
                            )}
                          </>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 text-xs">{fmtDate(r.offered_delivery_date) || "-"}</td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" title="Talep Listesine Git"
                                  onClick={() => nav(`/teklif-talepleri/santiye/${r.site_id || "merkez"}`)}
                                  data-testid={`offer-goto-rfqs-${i}`}
                                  className="h-7 text-[10px] rounded-none sm:rounded-sm">
                            <Handshake size={12} className="mr-1" /> Talepler
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEntry(r)}
                                  data-testid={`offer-enter-${i}`}
                                  className="h-7 text-[10px] rounded-none sm:rounded-sm">
                            Teklifi Gir
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openCompare(r.rfq_id)}
                                  data-testid={`offer-compare-${i}`}
                                  className="h-7 text-[10px] rounded-none sm:rounded-sm border-[#95C11F] text-[#4a6210]">
                            <ChartBar size={12} className="mr-1" /> Karşılaştır
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Teklif Gir dialog */}
      <Dialog open={!!entry} onOpenChange={(o) => !o && (setEntry(null), setEntryForm(null))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none sm:rounded-sm">
          {entry && entryForm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {entry.rfq.code} — {entry.row.supplier_name} Teklifi
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Firmanın gönderdiği teklifi PDF/fotoğraf olarak yükleyip AI ile otomatik doldurabilir veya manuel girebilirsiniz.
                </DialogDescription>
              </DialogHeader>

              {/* AI Teklif Yükleme */}
              <div className="mt-2 p-3 border border-dashed border-[#95C11F]/60 bg-[#F7FBE9]/60 rounded-none sm:rounded-sm flex items-center gap-3 flex-wrap">
                <Sparkle size={18} weight="duotone" className="text-[#95C11F]" />
                <div className="text-xs text-slate-700 flex-1 min-w-[180px]">
                  <div className="font-bold uppercase tracking-widest">Teklifi AI ile Aktar</div>
                  <div className="text-[11px] text-slate-500">PDF veya fotoğraf yükleyin — sistem birim fiyat, KDV, teslim tarihi, vade bilgilerini otomatik doldurur.</div>
                </div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    data-testid="entry-upload-file"
                    onChange={e => { const f = e.target.files?.[0]; if (f) parseOfferFile(f); e.target.value = ""; }}
                  />
                  <span className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-bold uppercase tracking-widest text-white rounded-none sm:rounded-sm ${parsing ? "bg-slate-400" : "bg-[#95C11F] hover:bg-[#7ea418]"}`}>
                    <UploadSimple size={14} weight="bold" />
                    {parsing ? "Analiz ediliyor..." : "Dosya Seç"}
                  </span>
                </label>
                {entryForm.attachment_path && (
                  <a
                    href={`${API}/files/${entryForm.attachment_path}`}
                    target="_blank" rel="noreferrer"
                    className="text-[11px] text-[#1B4776] underline"
                  >Yüklenen dosyayı gör</a>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Yanıt Durumu</Label>
                  <Select value={entryForm.response} onValueChange={v => setEntryForm(f => ({ ...f, response: v }))}>
                    <SelectTrigger className="mt-1 rounded-none sm:rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beklemede">Beklemede</SelectItem>
                      <SelectItem value="verildi">Verildi</SelectItem>
                      <SelectItem value="verilmedi">Verilmedi</SelectItem>
                      <SelectItem value="kabul_edildi">Kabul Edildi</SelectItem>
                      <SelectItem value="reddedildi">Reddedildi</SelectItem>
                    </SelectContent>
                  </Select>
                  {(() => {
                    const anyPrice = (entryForm.unit_prices || []).some(v => (parseFloat(v) || 0) > 0);
                    if (anyPrice && (entryForm.response || "beklemede") === "beklemede") {
                      return (
                        <div className="mt-1 text-[10px] text-emerald-700 flex items-center gap-1"
                             data-testid="entry-response-hint">
                          <CheckCircle size={11} weight="fill" />
                          Fiyat girildi — kayıtta otomatik <b>Verildi</b> olarak işaretlenecek.
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Teklif Tarihi</Label>
                  <Input type="date" value={entryForm.offered_delivery_date}
                         onChange={e => setEntryForm(f => ({ ...f, offered_delivery_date: e.target.value }))}
                         data-testid="entry-offer-date"
                         className="mt-1 rounded-none sm:rounded-sm" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Vade (Gün) — Mukayese İçin</Label>
                  <Input type="number" min="0" value={entryForm.payment_term_days}
                         onChange={e => setEntryForm(f => ({ ...f, payment_term_days: e.target.value }))}
                         data-testid="entry-payment-term-days"
                         placeholder="0 = peşin"
                         className="mt-1 rounded-none sm:rounded-sm" />
                  {entryForm._avg_vade_days && entryForm._avg_vade_series?.length >= 2 && (
                    <div className="mt-1 text-[10px] text-[#4a6210] bg-[#F7FBE9] border border-[#95C11F]/40 px-2 py-1 flex items-center gap-1"
                         data-testid="entry-avg-vade-hint">
                      <Sparkle size={10} weight="fill" className="text-[#95C11F]" />
                      AI: [{entryForm._avg_vade_series.join(", ")}] gün → <b>ortalama {entryForm._avg_vade_days} gün</b>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ödeme Koşulları (Serbest)</Label>
                  <Input value={entryForm.offered_payment_terms}
                         onChange={e => setEntryForm(f => ({ ...f, offered_payment_terms: e.target.value }))}
                         placeholder="Örn: %30 peşin, kalanı 60 gün vade"
                         className="mt-1 rounded-none sm:rounded-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Termin/Çekim Süresi (Gün)</Label>
                  <Input type="number" min="0" value={entryForm.lead_time_days}
                         onChange={e => setEntryForm(f => ({ ...f, lead_time_days: e.target.value }))}
                         data-testid="entry-lead-time-days"
                         placeholder="Sipariş sonrası şantiyeye ulaşma süresi (gün)"
                         className="mt-1 rounded-none sm:rounded-sm max-w-[280px]" />
                </div>
              </div>

              {/* Malzeme kalemleri: Birim Fiyat + KDV */}
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Kalem Bazlı Birim Fiyat &amp; KDV</Label>
                  <button
                    type="button"
                    onClick={() => setShowAddVat(v => !v)}
                    className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776] hover:underline flex items-center gap-1"
                    data-testid="entry-add-vat-toggle"
                  >
                    <Plus size={11} /> Yeni KDV Oranı
                  </button>
                </div>

                {showAddVat && (
                  <div className="mt-2 flex items-center gap-2 p-2 bg-slate-50 border border-slate-200">
                    <Input type="number" step="0.5" min="0" value={newVatRate}
                           onChange={e => setNewVatRate(e.target.value)}
                           placeholder="Örn: 8"
                           className="rounded-none sm:rounded-sm h-8 max-w-[120px]"
                           data-testid="entry-new-vat-input" />
                    <Button size="sm" onClick={addVatRate} data-testid="entry-new-vat-save"
                            className="h-8 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white text-[10px]">
                      Ekle
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowAddVat(false); setNewVatRate(""); }}
                            className="h-8 rounded-none sm:rounded-sm text-[10px]">İptal</Button>
                    <span className="text-[10px] text-slate-500">Mevcut oranlar: {vatRates.map(r => `%${r}`).join(", ")}</span>
                  </div>
                )}

                {(entry.rfq.items || []).length === 0 ? (
                  <div className="text-xs text-slate-400 mt-2">Bu RFQ&apos;de kalem yok.</div>
                ) : (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b bg-slate-50">
                          <th className="text-left py-2 pl-2 font-bold w-8">#</th>
                          <th className="text-left py-2 px-2 font-bold">Malzeme</th>
                          <th className="text-center py-2 px-2 font-bold w-16">Birim</th>
                          <th className="text-right py-2 px-2 font-bold w-20">Miktar</th>
                          <th className="text-right py-2 px-2 font-bold w-28">Birim Fiyat</th>
                          <th className="text-left py-2 px-2 font-bold w-32">Marka</th>
                          <th className="text-center py-2 px-2 font-bold w-20">KDV</th>
                          <th className="text-right py-2 px-2 font-bold w-28">Mal/Hizmet Toplam Tutar</th>
                          <th className="text-right py-2 px-2 font-bold w-28">KDV Dahil Toplam Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.rfq.items.map((it, i) => {
                          const up = parseFloat(entryForm.unit_prices[i]) || 0;
                          const qty = parseFloat(it.quantity) || 0;
                          const net = up * qty;
                          const vr = parseFloat(entryForm.vat_rates[i]) || 0;
                          const gross = net * (1 + vr / 100);
                          const mi = matchedInfo.find(m => m.index === i);
                          const qtyDiff = mi?.matched ? (mi.quantity_diff_pct || 0) : 0;
                          const rowWarn = mi?.matched && Math.abs(qtyDiff) > 0;
                          return (
                            <tr key={i} className={`border-b border-slate-100 ${rowWarn ? "bg-amber-50/40" : ""}`}>
                              <td className="py-1.5 pl-2 text-slate-400 font-mono text-xs">{i + 1}</td>
                              <td className="py-1.5 px-2 text-slate-800">
                                {it.material_name}
                                {mi?.matched && mi.ai_item?.material_name &&
                                  mi.ai_item.material_name.toLowerCase() !== it.material_name.toLowerCase() && (
                                    <div className="text-[10px] text-amber-700 font-medium mt-0.5 flex items-center gap-1">
                                      <Sparkle size={10} weight="fill" />
                                      AI eşleşme: &quot;{mi.ai_item.material_name}&quot;
                                      {mi.score < 100 && <span className="text-slate-400">({mi.score}%)</span>}
                                    </div>
                                  )}
                                {rowWarn && (
                                  <div className="text-[10px] text-red-700 font-medium mt-0.5 flex items-center gap-1">
                                    <Warning size={10} weight="fill" />
                                    Miktar farkı: talep {mi.rfq_quantity} → teklif {mi.ai_quantity} ({qtyDiff > 0 ? "+" : ""}{qtyDiff}%)
                                  </div>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-center text-slate-600 text-xs">{it.unit}</td>
                              <td className="py-1.5 px-2 text-right font-mono text-slate-700">{qty}</td>
                              <td className="py-1.5 px-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={entryForm.unit_prices[i]}
                                  onChange={e => setEntryForm(f => {
                                    const arr = [...f.unit_prices]; arr[i] = e.target.value;
                                    const priced = arr.some(v => (parseFloat(v) || 0) > 0);
                                    const autoV = priced && ["", "beklemede", "verilmedi"].includes(f.response || "");
                                    return { ...f, unit_prices: arr, ...(autoV ? { response: "verildi" } : {}) };
                                  })}
                                  data-testid={`entry-price-${i}`}
                                  className="rounded-none sm:rounded-sm text-right font-mono h-9"
                                />
                              </td>
                              <td className="py-1.5 px-2">
                                <Input
                                  value={entryForm.brands?.[i] || ""}
                                  onChange={e => setEntryForm(f => {
                                    const arr = [...(f.brands || [])];
                                    while (arr.length <= i) arr.push("");
                                    arr[i] = e.target.value;
                                    return { ...f, brands: arr };
                                  })}
                                  placeholder="Marka / Model"
                                  data-testid={`entry-brand-${i}`}
                                  maxLength={80}
                                  className="rounded-none sm:rounded-sm text-xs h-9"
                                />
                              </td>
                              <td className="py-1.5 px-2">
                                <Select
                                  value={String(entryForm.vat_rates[i] ?? 20)}
                                  onValueChange={v => setEntryForm(f => {
                                    const arr = [...f.vat_rates]; arr[i] = parseFloat(v);
                                    return { ...f, vat_rates: arr };
                                  })}
                                >
                                  <SelectTrigger className="mt-0 h-9 rounded-none sm:rounded-sm text-xs" data-testid={`entry-vat-${i}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {vatRates.map(r => (
                                      <SelectItem key={r} value={String(r)}>%{r}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono text-slate-800">
                                {net > 0 ? fmtTL(net) : "-"}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono font-bold text-slate-900">
                                {net > 0 ? fmtTL(gross) : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50 border-t border-slate-300">
                          <td colSpan={6} className="py-2 px-2 text-right font-bold uppercase tracking-widest text-slate-700 text-xs">Mal/Hizmet Toplam Tutarı</td>
                          <td className="py-2 px-2 text-right font-mono font-bold text-slate-900" data-testid="entry-mat-net">
                            {fmtTL(entryLiveCalc.matNet)}
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-slate-700">
                            {fmtTL(entryLiveCalc.matNet + entryLiveCalc.matVat)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* NAKLİYE BÖLÜMÜ */}
              <div className="mt-5 p-3 border border-slate-200 rounded-none sm:rounded-sm bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Truck size={16} weight="duotone" className="text-[#1B4776]" />
                    <span className="text-[11px] uppercase tracking-widest font-bold text-slate-700">Nakliye</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="ship-inc" className="text-[11px] text-slate-700 font-medium cursor-pointer">
                      Fiyata Dahil
                    </Label>
                    <Switch id="ship-inc" checked={!!entryForm.shipping_included}
                            data-testid="entry-shipping-included"
                            onCheckedChange={v => setEntryForm(f => ({ ...f, shipping_included: v }))} />
                  </div>
                </div>

                {entryForm.shipping_included ? (
                  <div className="text-[11px] text-slate-500 italic flex items-center gap-1.5">
                    <Info size={12} /> Nakliye teklif fiyatına dahildir — ayrı hesaplanmayacak.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    <div className="col-span-2 md:col-span-1">
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Birim</Label>
                      <Select value={entryForm.shipping_unit}
                              onValueChange={v => setEntryForm(f => ({ ...f, shipping_unit: v }))}>
                        <SelectTrigger className="mt-1 h-9 rounded-none sm:rounded-sm text-xs" data-testid="entry-shipping-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["sefer", "torba", "adet", "ton", "palet", "km", "m³"].map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Birim Fiyat (TL)</Label>
                      <Input type="number" step="0.01" min="0" value={entryForm.shipping_unit_price}
                             data-testid="entry-shipping-unit-price"
                             onChange={e => setEntryForm(f => ({ ...f, shipping_unit_price: e.target.value }))}
                             className="mt-1 rounded-none sm:rounded-sm h-9 text-right font-mono" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Miktar</Label>
                      <Input type="number" step="0.01" min="0" value={entryForm.shipping_quantity}
                             data-testid="entry-shipping-quantity"
                             onChange={e => setEntryForm(f => ({ ...f, shipping_quantity: e.target.value }))}
                             className="mt-1 rounded-none sm:rounded-sm h-9 text-right font-mono" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">KDV</Label>
                      <Select value={String(entryForm.shipping_vat_rate ?? 20)}
                              onValueChange={v => setEntryForm(f => ({ ...f, shipping_vat_rate: parseFloat(v) }))}>
                        <SelectTrigger className="mt-1 h-9 rounded-none sm:rounded-sm text-xs" data-testid="entry-shipping-vat">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {vatRates.map(r => (
                            <SelectItem key={r} value={String(r)}>%{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Nakliye Vadesi (Gün)</Label>
                      <Input type="number" min="0" value={entryForm.shipping_payment_term_days}
                             data-testid="entry-shipping-payment-term-days"
                             onChange={e => setEntryForm(f => ({ ...f, shipping_payment_term_days: e.target.value }))}
                             placeholder="0 = malzeme vadesiyle aynı"
                             className="mt-1 rounded-none sm:rounded-sm h-9" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Nakliye Toplam</Label>
                      <div className="mt-1 h-9 flex items-center justify-end px-2 border border-slate-200 bg-slate-50 font-mono font-bold text-[#1B4776]" data-testid="entry-shipping-total">
                        {fmtTL(entryLiveCalc.shipNet)}
                      </div>
                    </div>
                    <div className="col-span-2 md:col-span-6">
                      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Nakliye Açıklama</Label>
                      <Input value={entryForm.shipping_note}
                             onChange={e => setEntryForm(f => ({ ...f, shipping_note: e.target.value }))}
                             placeholder="Ör: İstanbul içi teslim / Boşaltma bedeli hariç"
                             className="mt-1 rounded-none sm:rounded-sm h-9 text-xs" />
                    </div>
                  </div>
                )}
              </div>

              {/* Genel Toplam paneli */}
              <div className="mt-4 border border-[#1B4776]/30 bg-[#1B4776]/5 p-3">
                <div className="grid grid-cols-3 gap-2 text-right">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Mal/Hizmet Toplam Tutarı</div>
                    <div className="font-mono font-bold text-slate-900" data-testid="entry-net-total">{fmtTL(entryLiveCalc.net)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">KDV Toplamı</div>
                    <div className="font-mono text-slate-700" data-testid="entry-vat-total">{fmtTL(entryLiveCalc.vat)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776]">Genel Toplam KDV Dahil</div>
                    <div className="font-mono font-black text-[#1B4776] text-lg" data-testid="entry-gross-total">{fmtTL(entryLiveCalc.gross)}</div>
                  </div>
                </div>
                {!entryForm.shipping_included && entryLiveCalc.shipNet > 0 && (
                  <div className="text-[10px] text-slate-500 mt-2 text-right">
                    (Malzeme: {fmtTL(entryLiveCalc.matNet)} + Nakliye: {fmtTL(entryLiveCalc.shipNet)})
                  </div>
                )}
              </div>

              <div className="mt-4">
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Not / Marka / Açıklama</Label>
                <Textarea value={entryForm.note} onChange={e => setEntryForm(f => ({ ...f, note: e.target.value }))}
                          rows={2} className="mt-1 rounded-none sm:rounded-sm" />
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => { setEntry(null); setEntryForm(null); }}
                        className="rounded-none sm:rounded-sm">Vazgeç</Button>
                <Button onClick={saveEntry} data-testid="entry-save"
                        className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                  <FloppyDisk size={14} className="mr-1.5" /> Kaydet
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Karşılaştırma dialog */}
      <Dialog open={!!compareData} onOpenChange={(o) => !o && (setCompareData(null), setCompareRfqId(null))}>
        <DialogContent className="w-[98vw] sm:w-[96vw] max-w-[1800px] h-[95vh] max-h-[95vh] p-0 overflow-hidden rounded-none sm:rounded-sm flex flex-col">
          {compareData && (
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
                      <Trophy size={18} weight="duotone" className="text-[#95C11F]" />
                      {compareData.rfq.code} — Teklif Karşılaştırma
                      {compareData.revision && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-none sm:rounded-sm">
                          Rev.{String(compareData.revision).padStart(2, "0")}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={createNewRevision}
                        data-testid="compare-new-revision-btn"
                        title="Yeni müzakere turu için Rev.NN+1 açar. Kalem/firma korunur, fiyatlar sıfırlanır."
                        className="inline-flex items-center gap-1 h-6 px-2 text-[10px] uppercase tracking-widest font-bold text-white bg-[#1B4776] hover:bg-[#153a63] rounded-none sm:rounded-sm border border-[#1B4776]"
                      >
                        <ArrowsClockwise size={11} weight="bold" /> Yeni Revizyon
                      </button>
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 mt-1">
                      Firmaların fiyat, KDV, teslim ve vade koşullarını yan yana inceleyin. Paranın zaman değerini hesaba katmak için hedef vade ve aylık faiz oranı girin.
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 mr-8">
                    <a
                      href={`${API}/rfqs/${compareRfqId}/compare/export?format=xlsx&target_vade_days=${tvmTarget || 0}&monthly_rate=${tvmMonthly || 0}&revision=${compareData.revision || 1}`}
                      target="_blank" rel="noreferrer"
                      data-testid="compare-export-xlsx"
                      className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] font-bold uppercase tracking-widest text-white bg-emerald-700 hover:bg-emerald-800 rounded-none sm:rounded-sm"
                    >
                      <FileXls size={13} weight="bold" /> Excel
                    </a>
                    <a
                      href={`${API}/rfqs/${compareRfqId}/compare/export?format=pdf&target_vade_days=${tvmTarget || 0}&monthly_rate=${tvmMonthly || 0}&revision=${compareData.revision || 1}`}
                      target="_blank" rel="noreferrer"
                      data-testid="compare-export-pdf"
                      className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] font-bold uppercase tracking-widest text-white bg-red-700 hover:bg-red-800 rounded-none sm:rounded-sm"
                    >
                      <FilePdf size={13} weight="bold" /> PDF
                    </a>
                  </div>
                </div>
              </DialogHeader>

              {/* Fiyat yoksa empty-state banner */}
              {compareData.totals.every(t => (t?.net || 0) === 0) && (
                <div className="mt-3 p-4 border-2 border-dashed border-amber-400 bg-amber-50 rounded-none sm:rounded-sm flex items-start gap-3"
                     data-testid="compare-empty-state">
                  <Warning size={22} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-amber-900">Bu RFQ&apos;da henüz fiyat girilmemiş</div>
                    <div className="text-xs text-amber-700 mt-1">
                      Karşılaştırma yapabilmek için önce firmaların birim fiyatlarını girmelisiniz.
                      Dialogu kapatıp <b>Teklifi Gir</b> butonundan (veya AI ile PDF/foto yükleyerek) fiyatları ekleyin.
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => { setCompareData(null); setCompareRfqId(null); }}
                    data-testid="compare-empty-close"
                    className="shrink-0 h-8 rounded-none sm:rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold uppercase tracking-widest"
                  >
                    Kapat
                  </Button>
                </div>
              )}

              {/* TVM parametre paneli */}
              <div className="mt-2 p-3 bg-[#F7FBE9] border border-[#95C11F]/40 rounded-none sm:rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator size={14} weight="duotone" className="text-[#1B4776]" />
                  <span className="text-[11px] uppercase tracking-widest font-bold text-slate-700">Paranın Zaman Değeri (İsteğe Bağlı)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Hedef Vade (Gün)</Label>
                    <Input type="number" min="0" value={tvmTarget}
                           onChange={e => setTvmTarget(parseInt(e.target.value) || 0)}
                           onKeyDown={e => { if (e.key === "Enter") recomputeCompare(); }}
                           placeholder="120"
                           data-testid="compare-target-vade"
                           className="mt-1 rounded-none sm:rounded-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Aylık Faiz (%)</Label>
                    <Input type="number" step="0.1" min="0" value={tvmMonthly * 100 || ""}
                           onChange={e => setTvmMonthly((parseFloat(e.target.value) || 0) / 100)}
                           onKeyDown={e => { if (e.key === "Enter") recomputeCompare(); }}
                           placeholder="5"
                           data-testid="compare-monthly-rate"
                           className="mt-1 rounded-none sm:rounded-sm h-9" />
                  </div>
                  <div>
                    <Button
                      type="button"
                      onClick={recomputeCompare}
                      disabled={!hasAnyPrice}
                      data-testid="compare-tvm-calc"
                      title={hasAnyPrice ? "TVM'ye göre yeniden hesapla" : "Önce firma birim fiyatlarını girmelisiniz"}
                      className="w-full h-9 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest text-[11px]"
                    >
                      <Calculator size={14} className="mr-1.5" /> Hesapla
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <div className="text-[10px] text-slate-600 leading-relaxed">
                      <Info size={11} className="inline mr-1" />
                      Ayarlı Net = Net × (1 + <b>((hedef − firma_vade)/30)</b> × aylık_faiz).
                      {compareData.params?.use_adjusted
                        ? " Mukayese ayarlı net'e göre yapılıyor."
                        : " Değer girip Hesapla'ya basın."}
                    </div>
                  </div>
                </div>
              </div>

              {/* ================= FİYAT DIŞI UNSURLAR ================= */}
              <div className="mt-3 border border-slate-200 rounded-none sm:rounded-sm">
                <button
                  type="button"
                  onClick={() => setNpcPanelOpen(o => !o)}
                  data-testid="npc-panel-toggle"
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gradient-to-r from-[#F6F7F2] to-[#EEF2E0] hover:from-[#EEF2E0] hover:to-[#E4EBCE] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ClipboardText size={14} weight="duotone" className="text-[#4E7A0F]" />
                    <span className="text-[11px] uppercase tracking-widest font-bold text-slate-700">
                      Fiyat Dışı Unsurlar
                    </span>
                    {npcCriteria.length > 0 ? (
                      <span className="text-[10px] bg-[#4E7A0F] text-white px-1.5 py-0.5 rounded-none sm:rounded-sm font-bold">
                        {npcCriteria.length}
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-500 italic">
                        (tanımsız — sadece fiyat üzerinden değerlendirme)
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-600">{npcPanelOpen ? "▲ Kapat" : "▼ Aç"}</span>
                </button>

                {npcPanelOpen && (
                  <div className="p-3 bg-white border-t border-slate-200">
                    <div className="text-[10px] text-slate-600 mb-3 leading-relaxed">
                      <Info size={11} className="inline mr-1" />
                      <strong>Teknik kriterleri</strong> (kalite, sarfiyat, referans vb.) tanımlayın; her birine ağırlık (%) verin. Tip <strong>Öznel Puan</strong> ise 0-10 arası manuel puan, <strong>Sayısal Veri</strong> ise gerçek değer girin (ör: 2.5 kg/m²) — sistem z-score ile 0-10'a normalize eder. <strong>Mali skor</strong> ise TVM/vade dahil nihai fiyattan otomatik hesaplanır. Aşağıdaki grup ağırlıkları (Teknik/Mali) ile birleştirilerek <strong>Toplam AHP Skoru</strong> elde edilir — en yüksek skorlu firma "en uygun değer" (en ucuz fiyat olmayabilir).
                    </div>

                    {/* Faz 20.18 — Grup Ağırlıkları (Teknik / Mali) */}
                    <div className="mb-3 p-3 bg-[#F6F7F2] border border-[#4E7A0F]/30 rounded-none sm:rounded-sm"
                         data-testid="ahp-group-weights-panel">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[#4E7A0F] mb-2">
                        Grup Ağırlıkları (Toplam AHP için)
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-600">Teknik %</Label>
                          <Input
                            type="number" min={0} max={100} step={5}
                            value={ahpTeknikW}
                            onChange={e => {
                              const v = parseFloat(e.target.value) || 0;
                              setAhpTeknikW(v); setAhpMaliW(Math.max(0, 100 - v));
                            }}
                            onBlur={() => saveAhpWeights(ahpTeknikW, ahpMaliW)}
                            data-testid="ahp-teknik-weight"
                            disabled={ahpWeightSaving}
                            className="rounded-none sm:rounded-sm h-9 text-right font-mono font-bold text-emerald-800"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-600">Mali (Fiyat) %</Label>
                          <Input
                            type="number" min={0} max={100} step={5}
                            value={ahpMaliW}
                            onChange={e => {
                              const v = parseFloat(e.target.value) || 0;
                              setAhpMaliW(v); setAhpTeknikW(Math.max(0, 100 - v));
                            }}
                            onBlur={() => saveAhpWeights(ahpTeknikW, ahpMaliW)}
                            data-testid="ahp-mali-weight"
                            disabled={ahpWeightSaving}
                            className="rounded-none sm:rounded-sm h-9 text-right font-mono font-bold text-blue-800"
                          />
                        </div>
                        <div className="text-[11px] text-slate-600">
                          Toplam: <span className={`font-mono font-bold ${Math.abs((ahpTeknikW + ahpMaliW) - 100) < 0.5 ? "text-emerald-700" : "text-amber-700"}`}
                                        data-testid="ahp-weights-total">
                            %{(ahpTeknikW + ahpMaliW).toFixed(0)}
                          </span>
                          {" "}({ahpTeknikW}% teknik + {ahpMaliW}% fiyat)
                        </div>
                      </div>
                    </div>

                    {/* Mevcut kriterler tablosu */}
                    {npcCriteria.length > 0 && (() => {
                      const maliTotal = npcCriteria
                        .filter(c => (c.group || "teknik") === "mali")
                        .reduce((s, c) => s + (parseFloat(c.weight_pct) || 0), 0);
                      const teknikTotal = npcCriteria
                        .filter(c => (c.group || "teknik") === "teknik")
                        .reduce((s, c) => s + (parseFloat(c.weight_pct) || 0), 0);
                      const badge = (t) => {
                        const cls = Math.abs(t - 100) < 0.5
                          ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                          : t > 0
                            ? "bg-amber-100 text-amber-800 border-amber-300"
                            : "bg-slate-100 text-slate-600 border-slate-300";
                        return `inline-flex items-center px-1.5 py-0.5 border font-mono text-[10px] font-bold ${cls}`;
                      };
                      return (
                        <div className="mb-3 border border-slate-200">
                          <table className="w-full text-xs" data-testid="npc-criteria-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600 border-b border-slate-200">
                              <tr>
                                <th className="text-left px-2 py-1.5 font-bold w-20">Grup</th>
                                <th className="text-left px-2 py-1.5 font-bold">Kriter Adı</th>
                                <th className="text-left px-2 py-1.5 font-bold w-24">Tip</th>
                                <th className="text-left px-2 py-1.5 font-bold w-28">Yön / Birim</th>
                                <th className="text-right px-2 py-1.5 font-bold w-20">Ağırlık %</th>
                                <th className="px-2 py-1.5 w-8"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {npcCriteria.map((c) => {
                                const grp = c.group || "teknik";
                                const typ = c.type || "subjective";
                                return (
                                  <tr key={c.key} className="border-b border-slate-100 hover:bg-slate-50"
                                      data-testid={`npc-edit-row-${c.key}`}>
                                    <td className="px-2 py-1">
                                      <Select value={grp}
                                              onValueChange={(v) => updateNpcCriterion(c.key, { group: v })}>
                                        <SelectTrigger className="h-7 text-[11px] rounded-none sm:rounded-sm"
                                                       data-testid={`npc-group-${c.key}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="teknik">Teknik</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="px-2 py-1">
                                      <Input value={c.label}
                                             onChange={e => setCompareData(prev => prev ? {
                                               ...prev,
                                               rfq: {
                                                 ...prev.rfq,
                                                 non_price_criteria: prev.rfq.non_price_criteria.map(x =>
                                                   x.key === c.key ? { ...x, label: e.target.value } : x)
                                               }
                                             } : prev)}
                                             onBlur={() => updateNpcCriterion(c.key, { label: c.label })}
                                             className="h-7 text-[11px] rounded-none sm:rounded-sm"
                                             data-testid={`npc-label-${c.key}`} />
                                    </td>
                                    <td className="px-2 py-1">
                                      <Select value={typ}
                                              onValueChange={(v) => updateNpcCriterion(c.key, { type: v })}>
                                        <SelectTrigger className="h-7 text-[11px] rounded-none sm:rounded-sm"
                                                       data-testid={`npc-type-${c.key}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="subjective">Öznel Puan</SelectItem>
                                          <SelectItem value="numeric">Sayısal Veri</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="px-2 py-1">
                                      {typ === "numeric" ? (
                                        <div className="flex gap-1">
                                          <Select value={c.direction || "lower_better"}
                                                  onValueChange={(v) => updateNpcCriterion(c.key, { direction: v })}>
                                            <SelectTrigger className="h-7 text-[10px] rounded-none sm:rounded-sm px-1 w-14"
                                                           data-testid={`npc-dir-${c.key}`}
                                                           title="İyi olan yön">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="lower_better">▼ Az iyi</SelectItem>
                                              <SelectItem value="higher_better">▲ Çok iyi</SelectItem>
                                            </SelectContent>
                                          </Select>
                                          <Input value={c.unit || ""}
                                                 placeholder="birim"
                                                 maxLength={20}
                                                 onChange={e => setCompareData(prev => prev ? {
                                                   ...prev,
                                                   rfq: {
                                                     ...prev.rfq,
                                                     non_price_criteria: prev.rfq.non_price_criteria.map(x =>
                                                       x.key === c.key ? { ...x, unit: e.target.value } : x)
                                                   }
                                                 } : prev)}
                                                 onBlur={() => updateNpcCriterion(c.key, { unit: c.unit || "" })}
                                                 className="h-7 text-[10px] rounded-none sm:rounded-sm flex-1 min-w-0 px-1"
                                                 data-testid={`npc-unit-${c.key}`} />
                                        </div>
                                      ) : (
                                        <span className="text-[10px] italic text-slate-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-1">
                                      <Input type="number" min={0} max={100} step={1}
                                             value={c.weight_pct ?? 0}
                                             onChange={e => setCompareData(prev => prev ? {
                                               ...prev,
                                               rfq: {
                                                 ...prev.rfq,
                                                 non_price_criteria: prev.rfq.non_price_criteria.map(x =>
                                                   x.key === c.key ? { ...x, weight_pct: e.target.value } : x)
                                               }
                                             } : prev)}
                                             onBlur={() => updateNpcCriterion(c.key, { weight_pct: parseFloat(c.weight_pct) || 0 })}
                                             className="h-7 text-[11px] text-right font-mono rounded-none sm:rounded-sm"
                                             data-testid={`npc-weight-${c.key}`} />
                                    </td>
                                    <td className="px-2 py-1 text-center">
                                      <button type="button"
                                              onClick={() => removeNpcCriterion(c.key)}
                                              disabled={npcSaving}
                                              data-testid={`npc-remove-${c.key}`}
                                              className="text-red-600 hover:text-red-800 disabled:opacity-40 p-1">
                                        <X size={12} weight="bold" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t border-slate-200">
                              <tr>
                                <td colSpan={4} className="px-2 py-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-600">
                                  Grup Ağırlık Toplamları
                                </td>
                                <td className="px-2 py-1.5 text-right" data-testid="npc-total-weights">
                                  <div className="flex flex-col items-end gap-0.5">
                                    <span className={badge(maliTotal)} data-testid="npc-mali-total">Mali: %{maliTotal.toFixed(0)}</span>
                                    <span className={badge(teknikTotal)} data-testid="npc-teknik-total">Teknik: %{teknikTotal.toFixed(0)}</span>
                                  </div>
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Yeni kriter ekleme */}
                    <div className="flex items-stretch gap-2 flex-wrap">
                      <Select value="teknik" disabled>
                        <SelectTrigger className="h-9 w-24 rounded-none sm:rounded-sm text-[11px]"
                                       data-testid="npc-new-group">
                          <SelectValue>Teknik</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="teknik">Teknik</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={npcDraft}
                        onChange={e => setNpcDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addNpcCriterion(); } }}
                        placeholder="Kriter adı (ör: Kalite, Sarfiyat, Vade)"
                        data-testid="npc-new-input"
                        maxLength={120}
                        disabled={npcSaving}
                        className="flex-1 min-w-[160px] rounded-none sm:rounded-sm h-9"
                      />
                      <Select value={npcDraftType} onValueChange={setNpcDraftType}>
                        <SelectTrigger className="h-9 w-32 rounded-none sm:rounded-sm text-[11px]"
                                       data-testid="npc-new-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="subjective">Öznel Puan</SelectItem>
                          <SelectItem value="numeric">Sayısal Veri</SelectItem>
                        </SelectContent>
                      </Select>
                      {npcDraftType === "numeric" && (
                        <>
                          <Select value={npcDraftDir} onValueChange={setNpcDraftDir}>
                            <SelectTrigger className="h-9 w-28 rounded-none sm:rounded-sm text-[11px]"
                                           data-testid="npc-new-dir"
                                           title="İyi olan yön">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lower_better">▼ Az iyidir</SelectItem>
                              <SelectItem value="higher_better">▲ Çok iyidir</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            value={npcDraftUnit}
                            onChange={e => setNpcDraftUnit(e.target.value)}
                            placeholder="Birim (kg/m²)"
                            data-testid="npc-new-unit"
                            maxLength={20}
                            disabled={npcSaving}
                            className="w-28 rounded-none sm:rounded-sm h-9 text-[11px]"
                          />
                        </>
                      )}
                      <Input
                        type="number" min={0} max={100} step={1}
                        value={npcDraftWeight}
                        onChange={e => setNpcDraftWeight(e.target.value)}
                        placeholder="Ağırlık %"
                        data-testid="npc-new-weight"
                        disabled={npcSaving}
                        className="w-24 rounded-none sm:rounded-sm h-9 text-right font-mono"
                      />
                      <Button
                        type="button"
                        onClick={addNpcCriterion}
                        disabled={npcSaving || !npcDraft.trim()}
                        data-testid="npc-add-btn"
                        className="h-9 rounded-none sm:rounded-sm bg-[#4E7A0F] hover:bg-[#3d5f0c] disabled:bg-slate-300 text-white font-bold uppercase tracking-widest text-[11px] px-3"
                      >
                        <Plus size={14} className="mr-1" /> Ekle
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {/* ================= /FİYAT DIŞI UNSURLAR ================= */}

              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm border border-slate-200">
                  <thead>
                    <tr className="bg-[#1B4776] text-white">
                      <th className="text-left py-2 px-2 font-bold sticky left-0 bg-[#1B4776] z-10">Malzeme</th>
                      <th className="text-center py-2 px-2 font-bold">Miktar</th>
                      {compareData.recipients.map((r, j) => (
                        <th key={j} className={`text-center py-2 px-2 font-bold ${compareData.best_supplier_index === j ? "bg-emerald-600" : ""}`}>
                          {r.supplier_name}
                          {compareData.best_supplier_index === j && (
                            <div className="text-[9px] uppercase tracking-widest">
                              {compareData.params?.use_adjusted ? "En Uygun (Ayarlı)" : "En Uygun"}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Kabul Et satırı */}
                    <tr className="bg-white border-b-2 border-slate-200">
                      <td className="py-2 px-2 sticky left-0 bg-white text-[10px] uppercase tracking-widest font-bold text-slate-500">Karar</td>
                      <td></td>
                      {compareData.recipients.map((r, j) => {
                        const t = compareData.totals[j];
                        const canAccept = (t?.net || 0) > 0 && (t?.response !== "kabul_edildi") && (t?.response !== "reddedildi");
                        const isAccepted = compareData.recipients[j]?.response === "kabul_edildi";
                        return (
                          <td key={j} className="py-2 px-2 text-center">
                            {isAccepted ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold bg-emerald-100 text-emerald-800">
                                <CheckCircle size={11} weight="fill" /> Kabul Edildi
                              </span>
                            ) : canAccept ? (
                              <button
                                type="button"
                                onClick={() => acceptOffer(j)}
                                data-testid={`compare-accept-${j}`}
                                className="inline-flex items-center gap-1 px-2.5 h-7 text-[10px] uppercase tracking-widest font-bold text-white bg-[#95C11F] hover:bg-[#7ea418] rounded-none sm:rounded-sm"
                              >
                                <Handshake size={11} weight="bold" /> Kabul Et
                              </button>
                            ) : (
                              <span className="text-[10px] text-slate-400">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {compareData.matrix.map((row, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5 px-2 text-slate-800 sticky left-0 bg-white">{row.material_name}</td>
                        <td className="py-1.5 px-2 text-center font-mono text-slate-600 text-xs">
                          {row.quantity} {row.unit}
                        </td>
                        {row.prices.map((p, j) => {
                          const vr = row.vat_rates?.[j] || 0;
                          const brand = compareData.recipients?.[j]?.brands?.[i] || "";
                          return (
                            <td key={j} className={`py-1.5 px-2 text-right font-mono ${row.best_index === j ? "bg-emerald-50 font-bold text-emerald-800" : "text-slate-700"}`}>
                              {p != null && p > 0 ? (
                                <>
                                  {fmtTL(p)}
                                  {vr > 0 && <div className="text-[9px] text-slate-400 font-normal">KDV %{vr}</div>}
                                  {brand && (
                                    <div className="text-[10px] font-medium text-[#1B4776] normal-case tracking-normal mt-0.5"
                                         data-testid={`compare-brand-${i}-${j}`}
                                         title={`Marka: ${brand}`}>
                                      ★ {brand.length > 22 ? brand.slice(0,20) + "…" : brand}
                                    </div>
                                  )}
                                </>
                              ) : <span className="text-slate-300">-</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    <tr className="bg-slate-50 border-t-2 border-slate-300 text-[10px] uppercase tracking-widest text-slate-500">
                      <td className="py-1.5 px-2 font-bold sticky left-0 bg-slate-50">Malzeme Net</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className={`py-1.5 px-2 text-right font-mono text-slate-800`}>
                          {t.material_net > 0 ? fmtTL(t.material_net) : "-"}
                        </td>
                      ))}
                    </tr>
                    {compareData.totals.some(t => !t.shipping_included && t.shipping_net > 0) && (
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-t border-slate-200">
                        <td className="py-1.5 px-2 font-bold sticky left-0 bg-white flex items-center gap-1">
                          <Truck size={11} weight="duotone" className="text-[#1B4776]" /> Nakliye Net
                        </td>
                        <td></td>
                        {compareData.totals.map((t, j) => (
                          <td key={j} className="py-1.5 px-2 text-right font-mono text-slate-700">
                            {t.shipping_included ? (
                              <span className="text-emerald-600 normal-case tracking-normal text-[10px]">dahil</span>
                            ) : t.shipping_net > 0 ? (
                              <>
                                {fmtTL(t.shipping_net)}
                                <div className="text-[9px] text-slate-400 font-normal normal-case tracking-normal">
                                  {t.shipping_unit_price ? `${fmtTL(t.shipping_unit_price)} × ${t.shipping_quantity} ${t.shipping_unit}` : ""}
                                </div>
                              </>
                            ) : "-"}
                          </td>
                        ))}
                      </tr>
                    )}
                    <tr className="bg-slate-100 font-bold border-t border-slate-300 text-[10px] uppercase tracking-widest">
                      <td className="py-2 px-2 text-slate-800 sticky left-0 bg-slate-100">Net Toplam</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className={`py-2 px-2 text-right font-mono font-bold text-slate-900`}>
                          {t.net > 0 ? fmtTL(t.net) : "-"}
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <td className="py-1.5 px-2 sticky left-0 bg-slate-50">KDV</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className="py-1.5 px-2 text-right font-mono text-slate-600">
                          {t.vat > 0 ? fmtTL(t.vat) : "-"}
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-slate-100 font-bold border-t border-slate-300">
                      <td className="py-2 px-2 uppercase tracking-widest text-xs text-slate-700 sticky left-0 bg-slate-100">KDV Dahil Toplam</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className={`py-2 px-2 text-right font-mono ${(!compareData.params?.use_adjusted && compareData.best_supplier_index === j) ? "bg-emerald-100 text-emerald-900" : "text-slate-900"}`}>
                          {t.gross > 0 ? fmtTL(t.gross) : "-"}
                        </td>
                      ))}
                    </tr>

                    {compareData.params?.use_adjusted && (
                      <>
                        <tr className="bg-[#1B4776]/5 border-t-2 border-[#1B4776]/30 text-[10px] uppercase tracking-widest">
                          <td className="py-1.5 px-2 font-bold text-[#1B4776] sticky left-0 bg-[#eef3f9]">Malzeme Ayarlı (TVM)</td>
                          <td></td>
                          {compareData.totals.map((t, j) => (
                            <td key={j} className="py-1.5 px-2 text-right font-mono text-[#1B4776]">
                              {t.material_adjusted_net > 0 ? fmtTL(t.material_adjusted_net) : "-"}
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-[#1B4776]/5 text-[10px] text-slate-500">
                          <td className="py-1 px-2 sticky left-0 bg-[#eef3f9] pl-6">Malzeme Çarpan</td>
                          <td></td>
                          {compareData.totals.map((t, j) => (
                            <td key={j} className="py-1 px-2 text-right font-mono" data-testid={`compare-mult-mat-${j}`}>
                              ×{(t.material_multiplier || 1).toFixed(3)}
                            </td>
                          ))}
                        </tr>
                        {compareData.totals.some(t => !t.shipping_included && t.shipping_net > 0) && (
                          <>
                            <tr className="bg-[#1B4776]/5 text-[10px] uppercase tracking-widest">
                              <td className="py-1.5 px-2 font-bold text-[#1B4776] sticky left-0 bg-[#eef3f9] flex items-center gap-1">
                                <Truck size={10} weight="duotone" /> Nakliye Ayarlı (TVM)
                              </td>
                              <td></td>
                              {compareData.totals.map((t, j) => (
                                <td key={j} className="py-1.5 px-2 text-right font-mono text-[#1B4776]">
                                  {t.shipping_adjusted_net > 0 ? fmtTL(t.shipping_adjusted_net)
                                    : (t.shipping_included ? <span className="text-emerald-600 normal-case tracking-normal text-[10px]">dahil</span> : "-")}
                                </td>
                              ))}
                            </tr>
                            <tr className="bg-[#1B4776]/5 text-[10px] text-slate-500">
                              <td className="py-1 px-2 sticky left-0 bg-[#eef3f9] pl-6">Nakliye Çarpan</td>
                              <td></td>
                              {compareData.totals.map((t, j) => (
                                <td key={j} className="py-1 px-2 text-right font-mono" data-testid={`compare-mult-ship-${j}`}>
                                  {t.shipping_included ? "-" : `×${(t.shipping_multiplier || 1).toFixed(3)}`}
                                </td>
                              ))}
                            </tr>
                          </>
                        )}
                        <tr className="bg-[#1B4776]/10 border-t border-[#1B4776]/30 font-bold">
                          <td className="py-2 px-2 uppercase tracking-widest text-xs text-[#1B4776] sticky left-0 bg-[#dfe8f2]">
                            Ayarlı Toplam Net (TVM)
                          </td>
                          <td></td>
                          {compareData.totals.map((t, j) => (
                            <td key={j} data-testid={`compare-adjusted-${j}`}
                                className={`py-2 px-2 text-right font-mono ${compareData.best_supplier_index === j ? "bg-emerald-100 text-emerald-900" : "text-[#1B4776]"}`}>
                              {t.adjusted_net > 0 ? fmtTL(t.adjusted_net) : "-"}
                            </td>
                          ))}
                        </tr>
                      </>
                    )}

                    <tr className="text-xs text-slate-600 border-t border-slate-200">
                      <td className="py-1.5 px-2 sticky left-0 bg-white font-bold">Malzeme Vadesi (Gün)</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className="py-1.5 px-2 text-center font-mono">
                          {t.payment_term_days ? `${t.payment_term_days} gün` : "-"}
                        </td>
                      ))}
                    </tr>

                    {/* --- Fiyat Dışı Unsur satırları --- */}
                    {npcCriteria.length > 0 && (
                      <>
                        <tr className="bg-[#EEF2E0] border-t-2 border-[#4E7A0F]/30">
                          <td colSpan={2 + compareData.recipients.length}
                              className="py-1.5 px-2 text-[10px] uppercase tracking-widest font-bold text-[#4E7A0F] sticky left-0">
                            Fiyat Dışı Unsurlar — Puanlama (0-10) + Açıklama
                          </td>
                        </tr>
                        {npcCriteria.map((crit) => {
                          const grp = crit.group || "teknik";
                          const typ = crit.type || "subjective";
                          const isNumeric = typ === "numeric";
                          const grpBadge = grp === "mali"
                            ? "bg-blue-100 text-blue-800 border-blue-300"
                            : "bg-emerald-100 text-emerald-800 border-emerald-300";
                          const dirArrow = crit.direction === "higher_better" ? "▲" : "▼";
                          return (
                          <tr key={crit.key} className="text-xs bg-[#F6F7F2] border-b border-slate-200">
                            <td className="py-1.5 px-2 sticky left-0 bg-[#F6F7F2] font-medium text-slate-800"
                                data-testid={`npc-row-${crit.key}`}>
                              <div className="flex items-center gap-1.5">
                                <span className={`inline-flex items-center px-1 py-0.5 border text-[9px] font-bold uppercase tracking-widest ${grpBadge}`}>
                                  {grp === "mali" ? "M" : "T"}
                                </span>
                                <span className="truncate">{crit.label}</span>
                                {isNumeric && (
                                  <span className="text-[9px] px-1 py-0.5 bg-slate-200 text-slate-700 border border-slate-300 font-mono"
                                        title={crit.direction === "higher_better" ? "Yüksek daha iyi" : "Düşük daha iyi"}>
                                    {dirArrow}{crit.unit ? ` ${crit.unit}` : " sayısal"}
                                  </span>
                                )}
                                <span className="text-[9px] font-mono text-slate-500 ml-auto">%{crit.weight_pct ?? 0}</span>
                              </div>
                            </td>
                            <td></td>
                            {compareData.recipients.map((rec, j) => (
                              <td key={j} className="py-1 px-1 align-middle">
                                {isNumeric ? (
                                  <div className="flex flex-col gap-0.5">
                                    <Input
                                      type="number" step="any"
                                      value={(rec.non_price_numeric || {})[crit.key] ?? ""}
                                      onChange={e => setNpcNumeric(j, crit.key, e.target.value)}
                                      onBlur={() => persistNpcValues(j)}
                                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                                      placeholder={crit.unit || "değer"}
                                      data-testid={`npc-numeric-${crit.key}-${j}`}
                                      className="h-6 text-[11px] rounded-none sm:rounded-sm border-slate-300 focus:border-[#4E7A0F] text-center px-1 font-mono font-bold"
                                    />
                                    <Input
                                      value={(rec.non_price_values || {})[crit.key] || ""}
                                      onChange={e => setNpcValue(j, crit.key, e.target.value)}
                                      onBlur={() => persistNpcValues(j)}
                                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                                      placeholder="not"
                                      data-testid={`npc-value-${crit.key}-${j}`}
                                      className="h-6 text-[10px] rounded-none sm:rounded-sm border-slate-300 focus:border-[#4E7A0F] text-center px-1 text-slate-600"
                                    />
                                  </div>
                                ) : (
                                  <div className="flex flex-col gap-0.5">
                                    <Input
                                      type="number" min={0} max={10} step={0.5}
                                      value={(rec.non_price_scores || {})[crit.key] ?? ""}
                                      onChange={e => setNpcScore(j, crit.key, e.target.value)}
                                      onBlur={() => persistNpcValues(j)}
                                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                                      placeholder="0-10"
                                      data-testid={`npc-score-${crit.key}-${j}`}
                                      className="h-6 text-[11px] rounded-none sm:rounded-sm border-slate-300 focus:border-[#4E7A0F] text-center px-1 font-mono font-bold"
                                    />
                                    <Input
                                      value={(rec.non_price_values || {})[crit.key] || ""}
                                      onChange={e => setNpcValue(j, crit.key, e.target.value)}
                                      onBlur={() => persistNpcValues(j)}
                                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                                      placeholder="not / açıklama"
                                      data-testid={`npc-value-${crit.key}-${j}`}
                                      className="h-6 text-[10px] rounded-none sm:rounded-sm border-slate-300 focus:border-[#4E7A0F] text-center px-1 text-slate-600"
                                    />
                                  </div>
                                )}
                              </td>
                            ))}
                          </tr>
                          );
                        })}

                        {/* Hesaplanmış Mali & Teknik Skorlar */}
                        <tr className="text-xs bg-blue-50 border-t border-blue-200">
                          <td className="py-1.5 px-2 sticky left-0 bg-blue-50 font-bold text-blue-900" title="TVM/vade dahil nihai fiyattan z-score normalize (0-10). Düşük fiyat = yüksek skor.">
                            Mali Skor (fiyattan · 0-10)
                          </td>
                          <td></td>
                          {compareData.totals.map((t, j) => {
                            const v = t.mali_score;
                            const isBest = compareData.best_ahp_index === j && v != null;
                            return (
                              <td key={j}
                                  data-testid={`ahp-mali-${j}`}
                                  className={`py-1.5 px-2 text-center font-mono font-bold ${isBest ? "bg-blue-600 text-white" : "text-blue-900"}`}>
                                {v != null ? v.toFixed(2) : <span className="text-slate-400 font-normal">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="text-xs bg-emerald-50 border-b border-emerald-200">
                          <td className="py-1.5 px-2 sticky left-0 bg-emerald-50 font-bold text-emerald-900" title="Fiyat-dışı teknik kriterlerin ağırlıklı ortalaması (0-10).">
                            Teknik Skor (0-10)
                          </td>
                          <td></td>
                          {compareData.totals.map((t, j) => {
                            const v = t.teknik_score;
                            const isBest = compareData.best_ahp_index === j && v != null;
                            return (
                              <td key={j}
                                  data-testid={`ahp-teknik-${j}`}
                                  className={`py-1.5 px-2 text-center font-mono font-bold ${isBest ? "bg-emerald-600 text-white" : "text-emerald-900"}`}>
                                {v != null ? v.toFixed(2) : <span className="text-slate-400 font-normal">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                        {/* Toplam AHP Skoru — en uygun DEĞER */}
                        <tr className="text-xs bg-[#EEF2E0] border-b-2 border-[#4E7A0F]">
                          <td className="py-2 px-2 sticky left-0 bg-[#EEF2E0] font-bold uppercase tracking-widest text-[#1B4776] text-[11px]" title="Teknik ve Mali skorların grup ağırlıklarıyla birleşimi. En yüksek skor = en uygun değer (fiyat + kalite optimize).">
                            <div className="flex items-center gap-1.5">
                              <Trophy size={13} weight="fill" className="text-amber-500" />
                              Toplam AHP Skoru
                            </div>
                          </td>
                          <td></td>
                          {compareData.totals.map((t, j) => {
                            const v = t.ahp_overall_score;
                            const isBest = compareData.best_ahp_index === j && v != null;
                            return (
                              <td key={j}
                                  data-testid={`ahp-overall-${j}`}
                                  className={`py-2 px-2 text-center font-mono font-bold text-base ${isBest ? "bg-[#4E7A0F] text-white" : "text-[#1B4776]"}`}>
                                {v != null ? v.toFixed(2) : <span className="text-slate-400 font-normal">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                      </>
                    )}
                    {/* --- /Fiyat Dışı Unsur --- */}
                    {compareData.totals.some(t => !t.shipping_included) && (
                      <tr className="text-xs text-slate-600">
                        <td className="py-1.5 px-2 sticky left-0 bg-white font-bold">Nakliye Vadesi (Gün)</td>
                        <td></td>
                        {compareData.totals.map((t, j) => (
                          <td key={j} className="py-1.5 px-2 text-center font-mono">
                            {t.shipping_included ? <span className="text-emerald-600">dahil</span> : (t.shipping_payment_term_days ? `${t.shipping_payment_term_days} gün` : "-")}
                          </td>
                        ))}
                      </tr>
                    )}
                    <tr className="text-xs text-slate-600">
                      <td className="py-1.5 px-2 sticky left-0 bg-white">Ödeme Koşulları</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className="py-1.5 px-2 text-center text-[11px]">{t.offered_payment_terms || "-"}</td>
                      ))}
                    </tr>
                    <tr className="text-xs text-slate-600">
                      <td className="py-1.5 px-2 sticky left-0 bg-white">Teklif Tarihi</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className="py-1.5 px-2 text-center">{fmtDate(t.offered_delivery_date) || "-"}</td>
                      ))}
                    </tr>
                    <tr className="text-xs text-slate-600">
                      <td className="py-1.5 px-2 sticky left-0 bg-white">Termin/Çekim Süresi</td>
                      <td></td>
                      {compareData.totals.map((t, j) => (
                        <td key={j} className="py-1.5 px-2 text-center">{t.lead_time_days ? `${t.lead_time_days} gün` : "-"}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* --- AHP Radar / Örümcek Grafik --- */}
              {npcCriteria.length > 0 && (() => {
                // Her kriter için tek bir satır (subject = kriter adı), her firma için
                // ayrı bir Radar (dataKey). Skorlanmamış hücreler 0 kabul edilir.
                const chartData = npcCriteria.map((c) => {
                  const row = { subject: `${(c.group || "teknik") === "mali" ? "M · " : "T · "}${c.label}` };
                  compareData.recipients.forEach((rec, j) => {
                    // Backend'ten normalized 0-10 skorlar totals[j].ahp_per_criterion içinde
                    const perCrit = compareData.totals?.[j]?.ahp_per_criterion || {};
                    const s = perCrit[c.key];
                    row[`sup_${j}`] = (typeof s === "number") ? s : 0;
                  });
                  return row;
                });
                const palette = ["#1B4776", "#95C11F", "#D97706", "#0EA5E9", "#7C3AED", "#DC2626",
                                 "#059669", "#B45309", "#DB2777", "#4B5563"];
                const hasAny = compareData.totals?.some(t =>
                  Object.keys(t?.ahp_per_criterion || {}).length > 0
                );
                return (
                  <div className="mt-4 border border-[#4E7A0F]/30 rounded-none sm:rounded-sm bg-white"
                       data-testid="ahp-radar-panel">
                    <div className="px-3 py-2 bg-[#F6F7F2] border-b border-[#4E7A0F]/20 flex items-center gap-2">
                      <Polygon size={14} weight="duotone" className="text-[#4E7A0F]" />
                      <span className="text-[11px] uppercase tracking-widest font-bold text-slate-700">
                        Fiyat Dışı Unsurlar — Radar Grafik
                      </span>
                      <span className="text-[10px] text-slate-500 ml-2 italic">
                        Firmaların kriter bazında 0-10 puanları — dış hat ne kadar geniş, o firma o kadar iyi.
                      </span>
                    </div>
                    {!hasAny ? (
                      <div className="p-6 text-center text-xs text-slate-500">
                        Henüz herhangi bir firma için puan girilmedi. Yukarıdaki tabloda 0-10 arası puanları girin.
                      </div>
                    ) : (
                      <div className="p-3">
                        <div style={{ width: "100%", height: 380 }}>
                          <ResponsiveContainer>
                            <RadarChart data={chartData} outerRadius="75%">
                              <PolarGrid stroke="#cbd5e1" />
                              <PolarAngleAxis dataKey="subject"
                                              tick={{ fontSize: 10, fill: "#334155" }} />
                              <PolarRadiusAxis angle={90} domain={[0, 10]}
                                               tick={{ fontSize: 9, fill: "#64748b" }}
                                               tickCount={6} />
                              {compareData.recipients.map((rec, j) => (
                                <Radar
                                  key={j}
                                  name={rec.supplier_name || `Firma ${j + 1}`}
                                  dataKey={`sup_${j}`}
                                  stroke={palette[j % palette.length]}
                                  fill={palette[j % palette.length]}
                                  fillOpacity={0.18}
                                  strokeWidth={2}
                                />
                              ))}
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <Tooltip
                                formatter={(v, name) => [Number(v).toFixed(1), name]}
                                contentStyle={{ fontSize: 11, borderRadius: 0 }}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                        {compareData.best_ahp_index != null && (
                          <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-700 flex items-center gap-1.5 flex-wrap">
                            <Trophy size={13} weight="fill" className="text-amber-500" />
                            En uygun değer (Toplam AHP en yüksek):{" "}
                            <strong className="text-[#1B4776]" data-testid="ahp-best-name">
                              {compareData.recipients[compareData.best_ahp_index]?.supplier_name || "-"}
                            </strong>
                            {" — "}
                            <span className="font-mono">
                              Toplam: <strong>{compareData.totals[compareData.best_ahp_index]?.ahp_overall_score?.toFixed(2) ?? "—"}</strong>
                              {" · "}
                              Mali {compareData.totals[compareData.best_ahp_index]?.mali_score != null
                                ? compareData.totals[compareData.best_ahp_index].mali_score.toFixed(2) : "—"}
                              {" · "}
                              Teknik {compareData.totals[compareData.best_ahp_index]?.teknik_score != null
                                ? compareData.totals[compareData.best_ahp_index].teknik_score.toFixed(2) : "—"}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* --- /AHP Radar --- */}

              {/* Öngörülen Ödeme Takvimi */}
              {paymentTimeline && paymentTimeline.perSupplier.length > 0 && (
                <div className="mt-4 border border-[#1B4776]/20 rounded-none sm:rounded-sm bg-white" data-testid="payment-schedule">
                  <div className="px-3 py-2 bg-[#1B4776]/5 border-b border-[#1B4776]/20 flex items-center gap-2">
                    <Calendar size={14} weight="duotone" className="text-[#1B4776]" />
                    <span className="text-[11px] uppercase tracking-widest font-bold text-slate-700">
                      Öngörülen Ödeme Takvimi (KDV Dahil)
                    </span>
                    <span className="text-[10px] text-slate-500 ml-2 italic">
                      Vade metnindeki seri veya tek vade + nakliye vadesine göre ay bazında dağılım.
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                          <th className="text-left py-2 px-3 font-bold sticky left-0 bg-slate-50">Firma</th>
                          {paymentTimeline.months.map(m => (
                            <th key={m} className="text-right py-2 px-3 font-bold whitespace-nowrap">
                              {m === 0 ? "Peşin" : `Ay ${m}`}
                            </th>
                          ))}
                          <th className="text-right py-2 px-3 font-bold border-l border-slate-200 whitespace-nowrap">Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentTimeline.perSupplier.map(s => {
                          const isBest = compareData.best_supplier_index === s.j;
                          return (
                            <tr key={s.j} className={`border-b border-slate-100 ${isBest ? "bg-emerald-50/40" : ""}`}
                                data-testid={`payment-row-${s.j}`}>
                              <td className={`py-2 px-3 font-medium sticky left-0 ${isBest ? "bg-emerald-50/40 text-emerald-800" : "bg-white text-slate-800"}`}>
                                {s.name}
                                {isBest && <span className="ml-1.5 text-[9px] uppercase tracking-widest font-bold text-emerald-700">En Uygun</span>}
                              </td>
                              {paymentTimeline.months.map(m => {
                                const amt = s.schedule[m] || 0;
                                return (
                                  <td key={m} className="py-2 px-3 text-right font-mono text-slate-700">
                                    {amt > 0 ? fmtTL(amt) : <span className="text-slate-300">-</span>}
                                  </td>
                                );
                              })}
                              <td className={`py-2 px-3 text-right font-mono font-bold border-l border-slate-200 ${isBest ? "text-emerald-900" : "text-slate-900"}`}>
                                {fmtTL(s.gross)}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Ay toplamları (nakit akışı özeti) */}
                        <tr className="bg-slate-100 border-t-2 border-slate-300 text-[10px] uppercase tracking-widest">
                          <td className="py-2 px-3 font-bold sticky left-0 bg-slate-100 text-slate-700">
                            Nakit Çıkışı Özeti
                          </td>
                          {paymentTimeline.months.map(m => {
                            const monthSum = paymentTimeline.perSupplier.reduce((a, s) => a + (s.schedule[m] || 0), 0);
                            return (
                              <td key={m} className="py-2 px-3 text-right font-mono font-bold text-[#1B4776]">
                                {fmtTL(monthSum)}
                              </td>
                            );
                          })}
                          <td className="py-2 px-3 text-right font-mono font-black text-[#1B4776] border-l border-slate-300">
                            {fmtTL(paymentTimeline.perSupplier.reduce((a, s) => a + s.gross, 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Firma Kayıt Modal — Kabul Et anında supplier_id yoksa */}
      <Dialog open={!!supplierModal} onOpenChange={(o) => !o && setSupplierModal(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none sm:rounded-sm">
          {supplierModal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Sparkle size={18} weight="duotone" className="text-[#95C11F]" />
                  Firma Kaydı Gerekli
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Bu firma henüz sistemde kayıtlı değil. Sözleşme oluşturabilmek için firma bilgilerini tamamlayın. Kaydettikten sonra Kabul Et akışı otomatik devam edecek.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {duplicateSupplier && (
                  <div className="md:col-span-2 p-3 border-2 border-amber-400 bg-amber-50 rounded-none sm:rounded-sm flex items-start gap-3"
                       data-testid="dup-supplier-warning">
                    <Warning size={20} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-widest font-bold text-amber-800">
                        Bu firma zaten sistemde kayıtlı
                      </div>
                      <div className="text-sm font-bold text-slate-900 mt-1">
                        {duplicateSupplier.match.name}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        {duplicateSupplier.reason === "vkn"
                          ? `VKN eşleşti: ${duplicateSupplier.match.tax_number}`
                          : "Firma adı ile eşleşme bulundu."}
                        {duplicateSupplier.match.tax_office && ` · ${duplicateSupplier.match.tax_office}`}
                        {duplicateSupplier.match.contact_person && ` · ${duplicateSupplier.match.contact_person}`}
                        {duplicateSupplier.match.phone && ` · ${duplicateSupplier.match.phone}`}
                      </div>
                    </div>
                    <Button
                      type="button"
                      onClick={useExistingSupplier}
                      data-testid="dup-use-existing"
                      className="shrink-0 h-8 rounded-none sm:rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold uppercase tracking-widest"
                    >
                      <CheckCircle size={12} className="mr-1" /> Mevcudu Kullan
                    </Button>
                  </div>
                )}
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Firma Adı (Marka) *</Label>
                  <Input
                    value={supplierModal.form.name}
                    onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, name: titleCaseTR(e.target.value) } }))}
                    data-testid="sup-name"
                    className="mt-1 rounded-none sm:rounded-sm"
                    placeholder="Örn: Bayındır Grup"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Firma Türü</Label>
                  <Select value={supplierModal.form.supplier_type}
                          onValueChange={v => setSupplierModal(s => ({ ...s, form: { ...s.form, supplier_type: v } }))}>
                    <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="sup-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uretici">Üretici</SelectItem>
                      <SelectItem value="bayi">Bayi</SelectItem>
                      <SelectItem value="tedarikci">Tedarikçi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ticari Unvan</Label>
                  <Input value={supplierModal.form.title}
                         onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, title: titleCaseTR(e.target.value) } }))}
                         data-testid="sup-title" className="mt-1 rounded-none sm:rounded-sm"
                         placeholder="Bayındır Grup İnş. San. Tic. A.Ş." />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Vergi Dairesi</Label>
                  <Input value={supplierModal.form.tax_office}
                         onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, tax_office: titleCaseTR(e.target.value) } }))}
                         data-testid="sup-tax-office" className="mt-1 rounded-none sm:rounded-sm" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Vergi No / VKN</Label>
                  <Input value={supplierModal.form.tax_number}
                         onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, tax_number: digitsOnly(e.target.value, 11) } }))}
                         inputMode="numeric" maxLength={11}
                         data-testid="sup-tax-number" className="mt-1 rounded-none sm:rounded-sm font-mono" placeholder="10 veya 11 haneli" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">İletişim Kişisi</Label>
                  <Input value={supplierModal.form.contact_person}
                         onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, contact_person: titleCaseTR(e.target.value) } }))}
                         data-testid="sup-contact" className="mt-1 rounded-none sm:rounded-sm" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Telefon</Label>
                  <Input value={supplierModal.form.phone}
                         onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, phone: e.target.value } }))}
                         data-testid="sup-phone" className="mt-1 rounded-none sm:rounded-sm" placeholder="+90 ..." />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">E-posta</Label>
                  <Input type="email" value={supplierModal.form.email}
                         onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, email: toLowerTR(e.target.value) } }))}
                         data-testid="sup-email" className="mt-1 rounded-none sm:rounded-sm" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Web Sitesi</Label>
                  <Input value={supplierModal.form.website}
                         onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, website: toLowerTR(e.target.value) } }))}
                         data-testid="sup-website" className="mt-1 rounded-none sm:rounded-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Adres</Label>
                  <Textarea value={supplierModal.form.address}
                            onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, address: titleCaseTR(e.target.value) } }))}
                            data-testid="sup-address" rows={2} className="mt-1 rounded-none sm:rounded-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Notlar</Label>
                  <Textarea value={supplierModal.form.notes}
                            onChange={e => setSupplierModal(s => ({ ...s, form: { ...s.form, notes: titleCaseTR(e.target.value) } }))}
                            data-testid="sup-notes" rows={2} className="mt-1 rounded-none sm:rounded-sm" />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                <Button variant="outline" onClick={() => setSupplierModal(null)}
                        className="rounded-none sm:rounded-sm">Vazgeç</Button>
                <Button onClick={saveSupplierAndContinue} data-testid="sup-save-continue"
                        className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea418] text-white">
                  <FloppyDisk size={14} className="mr-1.5" /> Kaydet ve Kabul&apos;e Devam Et
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
